// ===== Packages =====

import React, {
    useRef,
    useCallback,
    useMemo,
    useState,
    useEffect,
}                                       from 'react';
import {
    useParams,
    useNavigate,
    useLocation,
    useSearchParams,
}                                       from 'react-router-dom';
import {
    doc,
    onSnapshot,
    getFirestore,
    Unsubscribe,
}                                       from 'firebase/firestore';
import { ReactSVG }                     from 'react-svg';
import Stripe                           from 'stripe';
import {
    Elements,
}                                       from '@stripe/react-stripe-js';
import {
    loadStripe,
    Appearance,
    StripeElementsOptions,
}                                       from '@stripe/stripe-js';
import { v4 as uuidv4 }                 from 'uuid';

// ===== Firebase =====

import fire                             from '../../fire';

// ===== Components =====

import Breadcrumb                       from '../Breadcrumb';
import CheckoutCheckpoint               from '../CheckoutCheckpoint';

// ===== Hooks =====

import {
    useInterval,
    useLatest,
}                                       from '../../hooks';

// ===== Enums =====

import {
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    PAGE_ROUTE,
    USER_ACTION_TYPE,
    CHECKOUT_CHECKPOINT,
    FIRESTORE_COLLECTION,
    CHECKOUT_SESSION_STATUS_TYPE,
    STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE,
    STRIPE_PAYMENT_INTENT_STATUS,
    BILLING_ADDRESS_TYPE,
    STRIPE_PROMOTION_VALID_ITEM_TYPE,
    STRIPE_PROMOTION_VALIDATION_STATUS,
    TAX_RESOLUTION_PHASE,
    FAILURE_TELEMETRY_TYPE,
    FIREBASE_CALLABLE_ERROR_CODE,
}                                       from '../../enums';

// ===== Services =====

import {
    recordUserAction,
    updatePageTitle,
    detectTouchDevice,
    playAudio,
    applyPromotionToCheckoutItem,
    stripeCreatePaymentIntentWithCheckoutSession,
    stripeGetPaymentIntent,
    stripeUpdatePaymentIntent,
    stripeGetStoreItems,
    stripeApplyWebBookDiscount,
    stripeApplyPromotionCode,
    isSameCart,
    stripeGetPromotions,
    stripeGetTaxCalculationOrderCost,
    stripeGetPaymentMethod,
    stripeGetCustomerPaymentMethods,
    updateUser,
    getUserStripeCustomerId,
    getUserBookPurchases,
    getSaleItem,
    checkoutBootstrap,
    getCheckoutSessionItem,
    updateCheckoutSessionItem,
    getUserAddresses,
    roundToNDecimals,
    isTestMode,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
} from '../../services';
import resolveCheckoutPromotionReview                  from '../../services/resolveCheckoutPromotionReview';
import buildStoreItemsByProduct                        from '../../services/buildStoreItemsByProduct';
import useLazyAudioRef                                 from '../../services/useLazyAudioRef';
import {
    buildFallbackOrderCostFromPaymentIntent,
    calculateDiscountedCartSubtotal as calculateDiscountedCartSubtotalHelper,
} from '../../services/checkoutPricing';
import normalizePromotionCodeForValidation            from '../../services/normalizePromotionCodeForValidation';

// ===== Interfaces =====

import {
    IPartialUserItem,
    ISnackbarItem,
    IRadioItem,
    IDimension,
    ICartItem,
    IStoreItem,
    IShippoRateObject,
    IOrderCost,
    ISaleItem,
    IStripeApplyPromotionCodeResult,
    IStripePromotionValidItem,
    ICart,
    IAddress,
}                                       from '../../interfaces';
import {
    ICheckoutPromotionReviewResult,
    ICheckoutNegotiatedPricePreview,
    ICheckoutAppliedCoupon,
}                                       from '../../interfaces/ICheckoutPromotionReview';

// ===== Images =====

import VerascopeLogo                    from '../../images/verascope-logo-silhouette.svg';
import PixelCartIcon                    from '../../images/8-bit_cart.svg';
import PixelChevronIcon                 from '../../images/8-bit_chevron.svg';
import BoxIcon                          from '../../images/box.svg';
import Spinner                          from '../../images/editor/spinner.svg';
import PixelCrossIcon                   from '../../images/8-bit_cross.svg';

// ===== Sounds =====

import Success                          from '../../sounds/message_success.mp3';
import Delete                           from '../../sounds/delete.mp3';
import ButtonClick                      from '../../sounds/button_click.mp3';

// ===== Constants =====

import {
    HOVER_TARGET_CLASSNAME,
    DEFAULT_AUDIO_VOLUME,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    UNASSIGNED_ERROR_MESSAGE,
    CENTS_IN_A_DOLLAR,
    SUPPORT_EMAIL_ADDRESS,
    BODY_FONT_SIZE,
    PHYSICAL_BOOK_STRIPE_PRODUCT_TEST_API_ID,
    PHYSICAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
    DIGITAL_BOOK_STRIPE_PRODUCT_TEST_API_ID,
    DIGITAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
    DEFAULT_API_PING_VERSION_VALUE,
    DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID,
    DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID,
    PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID,
    PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID,
    MILLISECONDS_IN_A_SECOND,
    CHECKOUT_UPDATE_SKIPPED_STATUS_NOT_MUTABLE_METADATA_KEY,
}                                       from '../../constants/generalConstants';
import CURSOR_SIGN                      from '../../constants/cursorSigns';
import KEYCODE                          from '../../constants/keycodes';
import FONT_TYPE                        from '../../constants/fontType';
import MEDIA_QUERY_SIZE                 from '../../constants/mediaQuerySizes';

// ===== Styles =====

import {
    Container,
    ContentContainer,
    CheckpointContainer,
    OrderSummaryContainer,
    OrderSummary,
    Header,
    CartItemsContainer,
    BreadcrumbContainer,
    CartItemFinalQuantityValue,
    CartItemFinalQuantityContainer,
    OrderSummaryToggleButtonContainer,
    OrderSummaryToggleButton,
    PromotionCodeContainer,
    PromotionCodeHeader,
    PromotionCodeHeaderText,
    PromotionCodeToggleButtonContainer,
    PromotionCodeToggleButton,
    PromotionCodeBody,
    PromotionCodeInput,
    PromotionCodeAddButton,
    PromotionCodeBadgeContainer,
    PromotionCodeBadge,
    PromotionCodeBadgeRemoveButton,
    PlaceholderCheckpointContainer,
    PlaceholderCheckpointText,
    OrderSummaryDisclaimer,
}                                       from './styles';
import {
    HeaderText,
    CartItemsCountText,
    CartItems,
    CartItemContainer,
    CartItemImageContainer,
    CartItemImage,
    CartItemDetails,
    CartItemName,
    CartItemPrice,
    OrderSummaryEntries,
    OrderSummaryEntry,
    OrderSummaryText,
    OrderSummaryPrice,
    CartItemPriceAfterDiscount,
    DiscountedCartItemPrice,
    SpinnerContainer,
}                                       from '../CartView/styles';
import {
    PageLogo,
    PageSubtitle,
    PageTitle,
    PlaceholderBox,
}                                       from '../../styles';
import { theme }                        from '../../themes/theme-context';

// ===== Initialize Firebase =====

const { auth } = fire();

// ===== Initialize Stripe =====
// Make sure to call loadStripe outside of a component’s render to avoid
// recreating the Stripe object on every render.
// This is your test publishable API key.
const useStripeTestMode = isTestMode();
const STRIPE_PUBLISHABLE_KEY = useStripeTestMode
    ? process.env.REACT_APP_STRIPE_TEST_PUBLISHABLE_KEY
    : process.env.REACT_APP_STRIPE_LIVE_PUBLISHABLE_KEY;
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY!);

interface Props {
    customCursorActive: boolean,
    hasSound: boolean,
    user: IPartialUserItem | null,
    userEmail: string | null,
    userCartItems: ICartItem[] | undefined,
    currentSessionId: string | null,
    viewportDimensions: IDimension,
    snackbarData: ISnackbarItem,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    setIsUserProfileDialogExpanded: (isExpanded: boolean) => void,
    setInputFocused: React.Dispatch<React.SetStateAction<boolean>>,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setCursorIsHidden: React.Dispatch<React.SetStateAction<boolean>>,
}
function CheckoutView({
    customCursorActive,
    hasSound,
    user,
    userEmail,
    userCartItems,
    currentSessionId,
    viewportDimensions,
    snackbarData,
    setSnackbarData,
    setIsUserProfileDialogExpanded,
    setInputFocused,
    onCursorEnter,
    onCursorLeave,
    setCursorIsHidden,
}: Props): JSX.Element {
    // ===== General Constants =====

    // Snackbar Message when stripe unavailable
    const SNACKBAR_MESSAGE_CALCULATE_CART_SUBTOTAL_ERROR = `Unable to calculate cart subtotal. Please contact the verascope team for support at: ${
        SUPPORT_EMAIL_ADDRESS
    }.`;
    // Snackbar Message when encounter error generating payment intent
    const SNACKBAR_MESSAGE_STRIPE_PAYMENT_INTENT_CHECKOUT_SESSION_GENERAL_ERROR = (
        error: string,
    ): string => `Encountered an error creating checkout session: ${error}.`;
    const SNACKBAR_MESSAGE_STRIPE_PAYMENT_INTENT_USER_ERROR = 'Unable to relay checkout request to server because user\'s data cannot be accessed.';
    // Snackbar Message when encounter error creating checkout session
    const SNACKBAR_MESSAGE_RESUME_CHECKOUT_SESSION_ERROR = 'Unable to resume previous checkout session because user\'s data cannot be accessed.';
    // Snackbar Message when user submits an invalid promotion code
    const SNACKBAR_MESSAGE_INVALID_PROMOTION_CODE_ERROR = (code: string): string => `Promotion code '${code}' invalid.`;
    const SNACKBAR_MESSAGE_EXPIRED_PROMOTION_CODE_ERROR = (code: string): string => `Promotion code '${code}' expired.`;
    // Snackbar Message when user applies a valid promotion code
    const SNACKBAR_MESSAGE_APPLY_PROMOTION_CODE_SUCCESS = (code: string): string => `Successfully applied promotion code '${code}'!`;
    // Snackbar Message when user applies an invalid promotion code
    const SNACKBAR_MESSAGE_APPLY_PROMOTION_CODE_ERROR = (code: string): string => `Encountered error applying promotion code '${code}'.`;
    // Snackbar Message after payment submission
    const SNACKBAR_MESSAGE_SUBMIT_PAYMENT_SUCCESS = 'Payment succeeded!';
    // Snackbar Message after payment submission
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_SUBMIT_PAYMENT_PROCESSING = 'Your payment is processing and should complete momentarily. Please check your email inbox for further updates.';
    // Snackbar Message when payment remains processing too long after redirect
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_SUBMIT_PAYMENT_PROCESSING_TIMEOUT = `Your payment is still processing. Please contact ${SUPPORT_EMAIL_ADDRESS} if this doesn't resolve shortly.`;
    // Snackbar Message after payment submission
    const SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_REQUIRES_PAYMENT_METHOD = 'Your payment attempt failed. Please try again.';
    // Snackbar Message after payment submission
    const SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_REQUIRES_ACTION = 'Your payment requires authentication. Please consult the redirect page.';
    // Snackbar Message after payment submission
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_REQUIRES_CONFIRMATION = 'Your payment was not successful. Unable to confirm your purchase. Please try again.';
    // Snackbar Message after payment submission
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_REQUIRES_CAPTURE = 'Your payment was not successful. Unable to capture funds associated with your purchase. Please try again.';
    // Snackbar Message after payment submission
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_CANCELED = 'Your payment was not successful. Your checkout session has expired. Please refresh your page and try again.';
    // Snackbar Message after payment submission
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_GENERIC = `Payment failed. Please try again or contact the verascope team for support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    const CART_ITEM_NAME_PLACEHOLDER_WIDTH = 7.5 * BODY_FONT_SIZE;
    const CART_ITEM_NAME_PLACEHOLDER_HEIGHT = 0.9375 * BODY_FONT_SIZE;
    const CART_ITEM_PRICE_PLACEHOLDER_WIDTH = 3.125 * BODY_FONT_SIZE;
    const CART_ITEM_PRICE_PLACEHOLDER_HEIGHT = 0.9375 * BODY_FONT_SIZE;
    const ORDER_SUMMARY_PRICE_PLACEHOLDER_WIDTH = 3.75 * BODY_FONT_SIZE;
    const ORDER_SUMMARY_PRICE_PLACEHOLDER_HEIGHT = 1.25 * BODY_FONT_SIZE;
    const CHECKPOINT_PLACEHOLDER_ELLIPSIS_INTERVAL_DURATION = 1000;
    const POST_PAYMENT_PROCESSING_POLL_INTERVAL_DURATION = 15 * MILLISECONDS_IN_A_SECOND;
    const POST_PAYMENT_PROCESSING_MAX_WAIT_DURATION = 60 * MILLISECONDS_IN_A_SECOND;

    // ===== React Router =====

    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    // ===== Refs =====

    const PROMOTION_CODE_INPUT_REF_NAME = 'promotionCodeInputRef';
    const promotionCodeInputRef = useRef<HTMLInputElement>(null);

    // ----- Sound Clips
    const successClip = useLazyAudioRef({
        src: Success,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const deleteClip = useLazyAudioRef({
        src: Delete,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const buttonClickClip = useLazyAudioRef({
        src: ButtonClick,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const containerRef = useRef<HTMLDivElement>(null);
    const postPaymentCheckoutStatusReadErrorLoggedRef = useRef<boolean>(false);
    const succeededCartMetadataParseErrorLoggedRef = useRef<boolean>(false);
    const postPaymentStateSyncByLocationRef = useRef<string>('');
    const postPaymentProcessingStartedAtRef = useRef<number | null>(null);
    const postPaymentProcessingTimeoutNotifiedRef = useRef<boolean>(false);
    const autoAppliedPromotionCodeRef = useRef<string | null>(null);

    // ===== State =====

    // ----- General
    const [stripeCustomerId, setStripeCustomerId] = useState<string | undefined>(undefined);
    const [stripeClientSecret, setStripeClientSecret] = useState<string | undefined>(undefined);
    const [stripePaymentIntentId, setStripePaymentIntentId] = useState<string | undefined>(undefined);
    const [stripeTaxCalculationId, setStripeTaxCalculationId] = useState<string | undefined>(undefined);
    const [fetchingStripeCustomerId, setFetchingStripeCustomerId] = useState<boolean>(false);
    const [fetchingUserAddresses, setFetchingUserAddresses] = useState<boolean>(false);
    // Stores the location key for which a view action has been recorded.
    const recordedViewPageUserActionKey = useRef<string | null>(null);
    // Indicates whether user has bought a web book
    const [purchasedWebBookSessionIds, setPurchasedWebBookSessionIds] = useState<string[]>([]);
    // Indicates whether user has bought a digital book
    const [purchasedDigitalBookSessionIds, setPurchasedDigitalBookSessionIds] = useState<string[]>([]);
    // Indicates whether user has bought a physical book
    const [purchasedPhysicalBookSessionIds, setPurchasedPhysicalBookSessionIds] = useState<string[]>([]);
    // Indicates whether checked for book purchases
    const [checkedBookPurchases, setCheckedBookPurchases] = useState<boolean>(false);
    // Indicates whether checked for customer payment methods
    const [checkedPaymentMethods, setCheckedPaymentMethods] = useState<boolean>(false);
    // Animates placeholder text when loading checkpoint
    const [checkpointPlaceholderEllipsis, setCheckpointPlaceholderEllipsis] = useState<string>('...');
    // Indicated whether checkpoint placeholder text is running
    const [isRunningCheckpointPlaceholderInterval, setIsRunningCheckpointPlaceholderInterval] = useState<boolean>(true);
    // Indicates the value of the last sale item update version we've received to compare against new updates
    const [lastSaleItemPingVersion, setLastSaleItemPingVersion] = useState<number>(DEFAULT_API_PING_VERSION_VALUE);

    // ----- Promotion Code
    // Indicates whether the promotion code input is visible
    const [promotionCodeInputIsVisible, setPromotionCodeInputIsVisible] = useState<boolean>(false);
    // Stores promotion code value
    const [promotionCode, setPromotionCode] = useState<string>('');
    // Indicates whether promotion code is being validated
    const [isValidatingPromotionCode, setIsValidatingPromotionCode] = useState<boolean>(false);
    // Indicates whether promotion code is invalid
    const [promotionCodeInvalid, setPromotionCodeInvalid] = useState<boolean>(false);
    // Stores applied promotion code ids
    const [appliedPromotions, setAppliedPromotions] = useState<Stripe.Coupon[]>([]);

    // ----- Store Items
    // Stores a map of store items
    const [storeItemByPrice, setStoreItemByPrice] = useState<Map<string, IStoreItem>>(new Map());
    // Indicates whether we're currently fetching store items
    const [fetchingStoreItems, setFetchingStoreItems] = useState<boolean>(false);
    // Indicates whether store items have been fetched
    const [fetchedStoreItems, setFetchedStoreItems] = useState<boolean>(false);
    // Indicates whether attempted to restore checkout session after payment
    const [attemptedRestoringCheckoutSessionAfterPayment, setAttemptedRestoringCheckoutSessionAfterPayment] = useState<boolean>(false);
    // Indicates whether attempted to resume previous checkout session
    const [attemptedResumingPreviousCheckoutSession, setAttemptedResumingPreviousCheckoutSession] = useState<boolean>(false);
    // Stores current checkout session
    const [currentCheckoutSessionId, setCurrentCheckoutSessionId] = useState<string | undefined>(undefined);
    // Indicates whether a physical item is in cart
    const [physicalItemsInCart, setPhysicalItemsInCart] = useState<string[]>([]);
    // Stores store item information of web book
    const [webBookStoreItem, setWebBookStoreItem] = useState<IStoreItem | undefined>(undefined);

    // Price previews stay local until eligibility is checked on the way to review.
    const [negotiationPreview, setNegotiationPreview] = useState<ICheckoutNegotiatedPricePreview>();
    const manualCouponCodeRef = useRef<ICheckoutAppliedCoupon>();
    // ----- Order Summary
    // Indicates whether order summary is visible on small screens
    const [orderSummaryIsVisible, setOrderSummaryIsVisible] = useState<boolean>(false);
    // Stores order cost details
    const [orderCost, setOrderCost] = useState<IOrderCost | undefined>(undefined);
    // Preserves complimentary Book Invite eligibility while review payment processing updates auth state.
    const [hadComplimentaryBookInviteForCheckoutFlow, setHadComplimentaryBookInviteForCheckoutFlow] = useState<boolean>(false);

    // ----- Info
    // Stores a list of saved addresses
    const [savedAddresses, setSavedAddresses] = useState<IAddress[]>([]);
    // Indicates whether address is valid
    const [addressIsValid, setAddressIsValid] = useState<boolean>(true);
    // Stores street address line one of customer
    const [customerStreetOne, setCustomerStreetOne] = useState<string>('');

    // ----- Shipping Method
    // Stores a list of shipping rates
    const [shippingRates, setShippingRates] = useState<Map<string, IShippoRateObject>>(new Map());
    // Stores selected shipping method id
    const [selectedShippingRateID, setSelectedShippingRateID] = useState<string | undefined>(undefined);

    // ----- Billing
    // Stores customer's saved payment methods
    const [savedStripePaymentMethods, setSavedStripePaymentMethods] = useState<Stripe.PaymentMethod[]>([]);
    // Stores selected billing address Type
    const [selectedBillingAddressType, setSelectedBillingAddressType] = useState<IRadioItem | undefined>(undefined);
    // Indicates whether payment details are completed
    const [stripePaymentDetailsCompleted, setStripePaymentDetailsCompleted] = useState<boolean>(false);
    // Indicates whether payment went through
    const [stripePaymentSucceeded, setStripePaymentSucceeded] = useState<boolean>(false);
    // Indicates whether billing address is valid
    const [billingAddressIsValid, setBillingAddressIsValid] = useState<boolean>(true);
    // Stores street address line one of billing customer
    const [customerBillingStreetOne, setCustomerBillingStreetOne] = useState<string>('');
    // Indicates whether customer has completed all details
    const [customerDetailsCompleted, setCustomerDetailsCompleted] = useState<boolean>(false);

    // ----- Confirmation
    // Stores succeeded stripe payment intent
    const [stripeSucceededPaymentIntent, setStripeSucceededPaymentIntent] = useState<Stripe.PaymentIntent | undefined>(undefined);
    // Stores payment method of succeeded stripe payment intnet
    const [stripePaymentMethod, setStripePaymentMethod] = useState<Stripe.PaymentMethod | undefined>(undefined);
    // Indicates whether we should be listening for order sale item
    const [listenForSaleItem, setListenForSaleItem] = useState<boolean>(false);
    // Stores order sale item
    const [orderSaleItem, setOrderSaleItem] = useState<ISaleItem | undefined>(undefined);
    // Indicates whether we're fetching the sale order item
    const [fetchingOrderSaleItem, setFetchingOrderSaleItem] = useState<boolean>(false);
    // Prevent repeated surfacing of the same post-payment checkout failure state.
    const [postPaymentCheckoutErrorNotified, setPostPaymentCheckoutErrorNotified] = useState<boolean>(false);
    // If an order eventually resolves after the above error, show a one-time recovery notice.
    const [postPaymentCheckoutRecoveryNotified, setPostPaymentCheckoutRecoveryNotified] = useState<boolean>(false);
    // Tracks PI ids currently under post-payment processing monitoring.
    const [postPaymentProcessingIntentId, setPostPaymentProcessingIntentId] = useState<string | undefined>(undefined);
    // Manages tax-resolution polling as a single state machine.
    const [taxResolution, setTaxResolution] = useState<{
        phase: TAX_RESOLUTION_PHASE;
        cycle: number;
    }>({
        phase: TAX_RESOLUTION_PHASE.idle,
        cycle: 0,
    });
    const checkoutStoreItemFetchErrorLogRef = useRef<{
        signature: string,
        timestamp: number,
    } | null>(null);
    const CHECKOUT_STORE_ITEM_FETCH_ERROR_DEDUPE_WINDOW_MS = 2 * MILLISECONDS_IN_A_SECOND;

    // ===== Methods =====

    const onLogoEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.logomark,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onLogoLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onLogoClick = (e: React.MouseEvent): void => {
        onCursorLeave(e);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.clickHomeButton,
                sessionId: currentSessionId,
                payload: {
                    route: PAGE_ROUTE.checkout,
                },
            });
        }
        navigate(
            `/${PAGE_ROUTE.landing}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    /**
     * Records user action of viewing checkout page
     */
    const recordCheckoutPageUserAction = (): void => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.viewCheckoutPage,
                sessionId: currentSessionId,
            });
        }
    };

    const handlePlaceholderEllipsisText = (): void => {
        const currentEllipsis = checkpointPlaceholderEllipsis;
        const currentNumDots = currentEllipsis.split('.').length - 1;
        let updatedNumDots;
        if (currentNumDots === 3) {
            updatedNumDots = 0;
        } else {
            updatedNumDots = currentNumDots + 1;
        }

        setCheckpointPlaceholderEllipsis(`${'.'.repeat(updatedNumDots)}`);
    };

    const fetchStripeCustomerId = (): void => {
        if (!user) return;
        if (!fetchingStripeCustomerId) setFetchingStripeCustomerId(true);
        getUserStripeCustomerId({
            test: useStripeTestMode,
        }).then((customerId) => {
            setStripeCustomerId(customerId);
            setFetchingStripeCustomerId(false);
        });
    };

    const fetchUserAddressesLatest = useLatest({
        savedAddresses,
    });
    const fetchUserAddresses = (): void => {
        if (!user) return;
        if (!fetchingUserAddresses) setFetchingUserAddresses(true);
        getUserAddresses({
            test: useStripeTestMode,
        }).then((addresses) => {
            const cur = fetchUserAddressesLatest.current;
            if (
                addresses
                && addresses.length > 0
                && cur.savedAddresses.length !== addresses.length
            ) {
                setSavedAddresses(addresses);
            }
            setFetchingUserAddresses(false);
        });
    };

    const fetchStoreItemsLatest = useLatest({
        fetchingStoreItems,
        fetchedStoreItems,
    });
    const fetchStoreItems = (): void => {
        if (!user || !auth.currentUser) {
            if (fetchingStoreItems) setFetchingStoreItems(false);
            return;
        }
        if (!fetchingStoreItems) setFetchingStoreItems(true);
        let succeededMetadataCartItems: ICartItem[] = [];
        if (stripeSucceededPaymentIntent?.metadata?.cartItems) {
            try {
                succeededMetadataCartItems = JSON.parse(
                    stripeSucceededPaymentIntent.metadata.cartItems,
                ) as ICartItem[];
            } catch {
                succeededMetadataCartItems = [];
                if (
                    currentSessionId
                    && !succeededCartMetadataParseErrorLoggedRef.current
                ) {
                    succeededCartMetadataParseErrorLoggedRef.current = true;
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: 'Unable to parse checkout cart metadata after payment success.',
                        },
                    });
                }
            }
        }
        let sourceCartItems: ICartItem[] = [];
        if (userCartItems && userCartItems.length > 0) {
            sourceCartItems = userCartItems;
        } else if (succeededMetadataCartItems.length > 0) {
            sourceCartItems = succeededMetadataCartItems;
        }
        const negotiationRequestPriceIds: string[] = sourceCartItems
            .filter((item: ICartItem) => item.saleNegotiationPriceId != null)
            .map((item: ICartItem) => item.saleNegotiationPriceId!);
        stripeGetStoreItems({
            negotiationRequestPriceIds,
            paymentIntentId: stripePaymentIntentId,
            test: useStripeTestMode,
        }).then((itemsByPrice) => {
            const cur = fetchStoreItemsLatest.current;
            if (itemsByPrice) {
                setStoreItemByPrice(itemsByPrice);
                const itemsByProduct = buildStoreItemsByProduct({
                    itemsByPrice,
                    negotiationRequestPriceIds,
                });
                setWebBookStoreItem(itemsByProduct.get(WEB_BOOK_PRODUCT_ID));
                if (!cur.fetchedStoreItems) setFetchedStoreItems(true);
                if (cur.fetchingStoreItems) setFetchingStoreItems(false);
            }
        }).catch((error: unknown) => {
            const cur = fetchStoreItemsLatest.current;
            if (cur.fetchingStoreItems) setFetchingStoreItems(false);
            const errorCode = typeof error === 'object'
                && error !== null
                && 'code' in error
                ? (error as { code?: string }).code
                : undefined;
            const authTransitionError = errorCode === FIREBASE_CALLABLE_ERROR_CODE.unauthenticated
                || errorCode === FIREBASE_CALLABLE_ERROR_CODE.permissionDenied;
            const errorMessage = error instanceof Error
                ? error.message
                : String(error);
            const signature = `${errorCode || 'unknown'}:${errorMessage}`;
            const now = Date.now();
            const previousLog = checkoutStoreItemFetchErrorLogRef.current;
            if (
                previousLog
                && previousLog.signature === signature
                && now - previousLog.timestamp < CHECKOUT_STORE_ITEM_FETCH_ERROR_DEDUPE_WINDOW_MS
            ) {
                return;
            }
            checkoutStoreItemFetchErrorLogRef.current = {
                signature,
                timestamp: now,
            };
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: authTransitionError
                            ? `Store-item fetch error during auth transition in CheckoutView: ${errorCode || errorMessage}`
                            : `Unexpected store-item fetch error in CheckoutView: ${errorCode || errorMessage}`,
                    },
                });
            }
        });
    };

    const fetchSaleOrderItem = useCallback(async (): Promise<boolean> => {
        // eslint-disable-next-line max-len
        if (!stripePaymentIntentId) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Encountered a problem while fetching sale item. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            throw Error(`Encountered a problem while fetching sale item. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        if (!fetchingOrderSaleItem) setFetchingOrderSaleItem(true);
        return getSaleItem({
            test: useStripeTestMode,
            paymentIntentId: stripePaymentIntentId,
        }).then(async (saleItem) => {
            if (saleItem) {
                setOrderSaleItem(saleItem);
                setFetchingOrderSaleItem(false);
                return true;
            }

            // Surface terminal post-payment session failures so the user gets feedback
            // instead of waiting indefinitely with no sale item.
            try {
                const session = await getCheckoutSessionItem({
                    test: useStripeTestMode,
                    paymentIntentId: stripePaymentIntentId,
                });
                const latestStatus = session.status && session.status.length > 0
                    ? session.status[session.status.length - 1].value
                    : undefined;
                if (
                    latestStatus === CHECKOUT_SESSION_STATUS_TYPE.postPaymentFulfillmentError
                    && !postPaymentCheckoutErrorNotified
                ) {
                    setPostPaymentCheckoutErrorNotified(true);
                    const message = `Your payment was received, but we couldn't finalize your order yet. Please contact ${SUPPORT_EMAIL_ADDRESS}.`;
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: message,
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    if (currentSessionId) {
                        recordUserAction({
                            test: useStripeTestMode,
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                error: message,
                            },
                        });
                    }
                }
            } catch {
                // Non-blocking: continue waiting for sale item updates.
                if (
                    currentSessionId
                    && !postPaymentCheckoutStatusReadErrorLoggedRef.current
                ) {
                    postPaymentCheckoutStatusReadErrorLoggedRef.current = true;
                    const message = 'Unable to read checkout status while waiting for order fulfillment.';
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: message,
                        },
                    });
                }
            }

            // whether or not sale item is fetched, we need to make this false
            setFetchingOrderSaleItem(false);
            return false;
        });
    }, [
        stripePaymentIntentId,
        currentSessionId,
        fetchingOrderSaleItem,
        postPaymentCheckoutErrorNotified,
    ]);

    const resolveOrderCostFromTaxCalculation = (
        paymentIntent: Stripe.PaymentIntent,
    ): Promise<boolean> => {
        const { taxCalculationId } = paymentIntent.metadata;
        if (!taxCalculationId) {
            return Promise.resolve(false);
        }

        return stripeGetTaxCalculationOrderCost({
            test: useStripeTestMode,
            taxCalculationId,
        }).then((resolvedOrderCost) => {
            if (!resolvedOrderCost) {
                return false;
            }
            setOrderCost(resolvedOrderCost);
            return true;
        }).catch(() => {
            if (currentSessionId) {
                const message = 'Unable to resolve order cost from tax calculation.';
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: message,
                    },
                });
            }
            return false;
        });
    };

    // ** IMPORTANT **: Keep in sync with functions/src/stripeCallableHandlers.ts
    const VERASCOPE_APPLIES_ONCE_PER_ORDER_METADATA_KEY = 'verascopeAppliesOncePerOrder';

    /**
     * Returns whether a promotion should only be counted once across the entire
     * order, even when the cart contains multiple qualifying quantities.
     */
    const promotionAppliesOncePerOrder = useCallback((
        promotion: Stripe.Coupon,
    ): boolean => (
        promotion.metadata?.[VERASCOPE_APPLIES_ONCE_PER_ORDER_METADATA_KEY] === 'true'
    ), []);

    /**
     * Uses the server-owned payment-intent amount/tax metadata as the canonical
     * source of checkout pricing so promotion math never needs to be inferred
     * from coupon details in the browser.
     */
    const syncOrderCostFromPaymentIntent = useCallback((
        paymentIntent?: Stripe.PaymentIntent | null,
    ): Promise<void> => {
        if (!paymentIntent) return Promise.resolve();

        const fallbackOrderCost = buildFallbackOrderCostFromPaymentIntent(paymentIntent);
        if (paymentIntent.metadata?.taxCalculationId) {
            setStripeTaxCalculationId(paymentIntent.metadata.taxCalculationId);
            return resolveOrderCostFromTaxCalculation(paymentIntent).then((resolvedOrderCost) => {
                if (
                    !resolvedOrderCost
                    && fallbackOrderCost
                ) {
                    setOrderCost(fallbackOrderCost);
                }
            });
        }

        setStripeTaxCalculationId(undefined);
        if (fallbackOrderCost) setOrderCost(fallbackOrderCost);
        return Promise.resolve();
    }, [
        resolveOrderCostFromTaxCalculation,
    ]);

    /**
     * Mirrors Stripe's promotion math for fallback subtotal rendering and the
     * itemized order summary when the UI needs a per-item discount breakdown.
     */
    const calculateDiscountedCartSubtotal = useCallback(({
        cartItems,
        promotions,
        storeItemsByPrice,
    }: {
        cartItems: ICartItem[],
        promotions: Stripe.Coupon[],
        storeItemsByPrice: Map<string, IStoreItem>,
    }): number => calculateDiscountedCartSubtotalHelper({
        cartItems,
        promotions,
        storeItemsByPrice,
        promotionAppliesOncePerOrder,
    }), [
        promotionAppliesOncePerOrder,
    ]);

    const requestTaxResolutionPollingCycle = (): void => {
        // Incrementing cycle triggers a fresh polling pass without timers.
        setTaxResolution((cur) => ({
            phase: TAX_RESOLUTION_PHASE.polling,
            cycle: cur.cycle + 1,
        }));
    };

    const determineBookPurchases = (): void => {
        if (!user) return;
        if (!checkedBookPurchases) setCheckedBookPurchases(true);
        getUserBookPurchases({
            test: useStripeTestMode,
        }).then((purchases) => {
            setPurchasedWebBookSessionIds(purchases.web);
            setPurchasedDigitalBookSessionIds([...purchases.digitalPreorder, ...purchases.digital]);
            setPurchasedPhysicalBookSessionIds([...purchases.physicalPreorder, ...purchases.physical]);
        });
    };

    const determinePaymentMethods = (): void => {
        if (!checkedPaymentMethods) setCheckedPaymentMethods(true);
        if (
            user
            && stripeCustomerId
        ) {
            stripeGetCustomerPaymentMethods({
                test: useStripeTestMode,
                customerId: stripeCustomerId,
            }).then((paymentMethods) => {
                setSavedStripePaymentMethods(paymentMethods);
            });
        } else {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Unable to fetch payment methods. Missing customer information. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`Unable to fetch payment methods. Missing customer information. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
    };

    const calculateCartSubTotal = (): void => {
        if (
            userCartItems
            && userCartItems.length > 0
            && fetchedStoreItems
            && storeItemByPrice.size > 0
            && !orderCost
        ) {
            const subtotal = calculateDiscountedCartSubtotal({
                cartItems: userCartItems,
                promotions: appliedPromotions,
                storeItemsByPrice: storeItemByPrice,
            });
            setOrderCost({
                subtotal,
            });
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_CALCULATE_CART_SUBTOTAL_ERROR,
                icon: PixelCartIcon,
                hasFailure: true,
            });

            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: SNACKBAR_MESSAGE_CALCULATE_CART_SUBTOTAL_ERROR,
                    },
                });
            }
        }
    };

    const changeCheckoutSessionStatus = (status: CHECKOUT_SESSION_STATUS_TYPE): void => {
        const CHECKOUT_SESSION_STATUS_UPDATE_ERROR_MESSAGE = `Encounter a problem while updating checkout session. Please contact the verascope team for support at: ${SUPPORT_EMAIL_ADDRESS}.`;
        const reportCheckoutSessionStatusUpdateError = (message: string): void => {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: message,
                icon: PixelCartIcon,
                hasFailure: true,
            });

            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: message,
                    },
                });
            }
        };

        if (stripePaymentIntentId) {
            updateCheckoutSessionItem({
                test: useStripeTestMode,
                paymentIntentId: stripePaymentIntentId,
                statusesToAdd: [{
                    timestamp: Date.now(),
                    value: status,
                }],
            }).then((success) => {
                if (!success) {
                    reportCheckoutSessionStatusUpdateError(CHECKOUT_SESSION_STATUS_UPDATE_ERROR_MESSAGE);
                }
            }).catch(() => {
                reportCheckoutSessionStatusUpdateError(CHECKOUT_SESSION_STATUS_UPDATE_ERROR_MESSAGE);
            });
        } else {
            const message = `Unable to update checkout session status because it's unique identification number cannot be found. Please contact the verascope team for support at: ${SUPPORT_EMAIL_ADDRESS}.`;
            reportCheckoutSessionStatusUpdateError(message);
            throw Error(message);
        }
    };

    const reportCheckoutPaymentIntentUpdateError = (message: string): void => {
        // Centralized reporting so all PI-update failures show consistent UI + telemetry.
        setSnackbarData({
            visible: true,
            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
            text: message,
            icon: PixelCartIcon,
            hasFailure: true,
        });
        if (currentSessionId) {
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.error,
                sessionId: currentSessionId,
                payload: {
                    error: message,
                },
            });
        }
    };

    function parseMetadataJSON<T>(
        raw: string | undefined,
        fallback: T,
    ): T {
        if (!raw) return fallback;
        try {
            return JSON.parse(raw) as T;
        } catch {
            return fallback;
        }
    }

    const getPhysicalIdsFromPaymentIntent = (paymentIntent: Stripe.PaymentIntent): string[] => (
        parseMetadataJSON<string[]>(
            paymentIntent.metadata?.physicalIds,
            [],
        )
    );

    const getCartItemsFromPaymentIntent = (paymentIntent: Stripe.PaymentIntent): ICartItem[] => (
        parseMetadataJSON<ICartItem[]>(
            paymentIntent.metadata?.cartItems,
            [],
        )
    );

    const restoreCheckoutSessionAfterPaymentLatest = useLatest({
        stripePaymentSucceeded,
        changeCheckoutSessionStatus,
    });
    const restoreCheckoutSessionAfterPayment = (
        paymentIntentId: string,
    ): void => {
        // Get payment intent
        stripeGetPaymentIntent({
            test: useStripeTestMode,
            paymentIntentId,
        }).then((paymentIntent) => {
            const cur = restoreCheckoutSessionAfterPaymentLatest.current;
            switch (paymentIntent.status) {
            case STRIPE_PAYMENT_INTENT_STATUS.succeeded:
                if (postPaymentProcessingIntentId) {
                    setPostPaymentProcessingIntentId(undefined);
                    postPaymentProcessingStartedAtRef.current = null;
                    postPaymentProcessingTimeoutNotifiedRef.current = false;
                }
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_SUBMIT_PAYMENT_SUCCESS,
                    icon: PixelCartIcon,
                    hasSuccess: true,
                });
                if (!cur.stripePaymentSucceeded) {
                    setStripePaymentSucceeded(true);
                }
                break;
            case STRIPE_PAYMENT_INTENT_STATUS.processing:
                if (postPaymentProcessingIntentId !== paymentIntentId) {
                    setPostPaymentProcessingIntentId(paymentIntentId);
                    postPaymentProcessingStartedAtRef.current = Date.now();
                    postPaymentProcessingTimeoutNotifiedRef.current = false;
                }
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_SUBMIT_PAYMENT_PROCESSING,
                    icon: PixelCartIcon,
                    dismissable: true,
                });
                if (!cur.stripePaymentSucceeded) {
                    setStripePaymentSucceeded(true);
                }
                break;
            case STRIPE_PAYMENT_INTENT_STATUS.requiresPaymentMethod:
                setPostPaymentProcessingIntentId(undefined);
                postPaymentProcessingStartedAtRef.current = null;
                postPaymentProcessingTimeoutNotifiedRef.current = false;
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_REQUIRES_PAYMENT_METHOD,
                    icon: PixelCartIcon,
                    hasFailure: true,
                });
                // Change checkout status
                cur.changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.requiresPaymentMethod);
                if (cur.stripePaymentSucceeded) {
                    setStripePaymentSucceeded(false);
                }
                break;
            case STRIPE_PAYMENT_INTENT_STATUS.requiresAction:
                setPostPaymentProcessingIntentId(undefined);
                postPaymentProcessingStartedAtRef.current = null;
                postPaymentProcessingTimeoutNotifiedRef.current = false;
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_REQUIRES_ACTION,
                    icon: PixelCartIcon,
                    hasFailure: true,
                });
                // Change checkout status
                cur.changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.requiresAction);
                if (cur.stripePaymentSucceeded) {
                    setStripePaymentSucceeded(false);
                }
                break;
            case STRIPE_PAYMENT_INTENT_STATUS.requiresConfirmation:
                setPostPaymentProcessingIntentId(undefined);
                postPaymentProcessingStartedAtRef.current = null;
                postPaymentProcessingTimeoutNotifiedRef.current = false;
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_REQUIRES_CONFIRMATION,
                    icon: PixelCartIcon,
                    hasFailure: true,
                });
                // Change checkout status
                cur.changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.requiresConfirmation);
                if (cur.stripePaymentSucceeded) {
                    setStripePaymentSucceeded(false);
                }
                break;
            case STRIPE_PAYMENT_INTENT_STATUS.requiresCapture:
                setPostPaymentProcessingIntentId(undefined);
                postPaymentProcessingStartedAtRef.current = null;
                postPaymentProcessingTimeoutNotifiedRef.current = false;
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_REQUIRES_CAPTURE,
                    icon: PixelCartIcon,
                    hasFailure: true,
                });
                // Change checkout status
                cur.changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.requiresCapture);
                if (cur.stripePaymentSucceeded) {
                    setStripePaymentSucceeded(false);
                }
                break;
            case STRIPE_PAYMENT_INTENT_STATUS.canceled:
                setPostPaymentProcessingIntentId(undefined);
                postPaymentProcessingStartedAtRef.current = null;
                postPaymentProcessingTimeoutNotifiedRef.current = false;
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_CANCELED,
                    icon: PixelCartIcon,
                    hasFailure: true,
                });
                // Change checkout status
                cur.changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.abandoned);
                if (cur.stripePaymentSucceeded) {
                    setStripePaymentSucceeded(false);
                }
                break;
            default:
                setPostPaymentProcessingIntentId(undefined);
                postPaymentProcessingStartedAtRef.current = null;
                postPaymentProcessingTimeoutNotifiedRef.current = false;
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_GENERIC,
                    icon: PixelCartIcon,
                    hasFailure: true,
                });
                if (currentSessionId) {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: SNACKBAR_MESSAGE_SUBMIT_PAYMENT_ERROR_GENERIC,
                        },
                    });
                }
                // Change checkout status
                cur.changeCheckoutSessionStatus(CHECKOUT_SESSION_STATUS_TYPE.unknownIntentStatus);
                if (cur.stripePaymentSucceeded) {
                    setStripePaymentSucceeded(false);
                }
                break;
            }

            if (
                paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.succeeded
                || paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.processing
            ) {
                // Initialize post-payment checkout context for both succeeded and processing.
                // Processing intents can transition to succeeded shortly after redirect.
                setStripeSucceededPaymentIntent(paymentIntent);
                const physicalIds = getPhysicalIdsFromPaymentIntent(paymentIntent);
                setCurrentCheckoutSessionId(paymentIntentId);
                setPhysicalItemsInCart(physicalIds);
                if (physicalIds.length === 0) {
                    setSelectedBillingAddressType({
                        title: 'Use a different billing address',
                        value: BILLING_ADDRESS_TYPE.different,
                    });
                }

                if (paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.succeeded) {
                    // Store succeeded payment method when available.
                    stripeGetPaymentMethod({
                        test: useStripeTestMode,
                        paymentIntentId,
                    }).then((paymentMethod) => {
                        if (paymentMethod) setStripePaymentMethod(paymentMethod);
                    });
                }

                // Retrieve sale item (or start listening for fulfillment completion).
                getSaleItem({
                    test: useStripeTestMode,
                    paymentIntentId, // sales items share id with associated payment intents
                }).then((saleItem) => {
                    if (saleItem) {
                        setOrderSaleItem(saleItem);
                    } else {
                        setListenForSaleItem(true);
                    }

                    // Retrieve order cost
                    if (saleItem?.orderCost) {
                        setOrderCost(saleItem.orderCost);
                        setTaxResolution((taxResolutionState) => ({
                            phase: TAX_RESOLUTION_PHASE.resolved,
                            cycle: taxResolutionState.cycle,
                        }));
                    } else {
                        resolveOrderCostFromTaxCalculation(paymentIntent).then((resolvedOrderCost) => {
                            if (!resolvedOrderCost) {
                                requestTaxResolutionPollingCycle();
                            } else {
                                setTaxResolution((taxResolutionState) => ({
                                    phase: TAX_RESOLUTION_PHASE.resolved,
                                    cycle: taxResolutionState.cycle,
                                }));
                            }
                        });
                    }

                    stripeGetPromotions({
                        test: useStripeTestMode,
                        paymentIntentId,
                    }).then((promotionMap) => {
                        const promotions: Stripe.Coupon[] = Array.from(promotionMap.values());
                        const promotionIds: string[] = promotions.map((promotion) => promotion.id);
                        if (promotionIds.length > 0) {
                            // Apply promotions locally only if same cart
                            setAppliedPromotions(promotions);
                        }
                    });
                }).catch(() => {
                    // Keep listening if initial fetch fails transiently.
                    setListenForSaleItem(true);
                });
            }
        });

        if (!attemptedRestoringCheckoutSessionAfterPayment) {
            setAttemptedRestoringCheckoutSessionAfterPayment(true);
        }
    };
    const reconcileImmutableStatusSkippedUpdate = (paymentIntentId: string): void => {
        // Backend marked this update as skipped because PI tax/amount is immutable.
        // Reconcile from canonical PI state and continue through confirmation flow.
        if (currentSessionId) {
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.error,
                sessionId: currentSessionId,
                payload: {
                    // eslint-disable-next-line max-len
                    error: `Discount attempt skipped due to immutable payment intent status; reconciling checkout from payment intent ${paymentIntentId}.`,
                },
            });
        }
        restoreCheckoutSessionAfterPayment(paymentIntentId);
        navigate(
            `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.confirmation}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    const resumeCheckoutSessionLatest = useLatest({
        userCartItems,
    });
    const resumeCheckoutSession = (): void => {
        if (
            user
            && attemptedRestoringCheckoutSessionAfterPayment
            && !attemptedResumingPreviousCheckoutSession
            && !currentCheckoutSessionId
            && !stripePaymentIntentId
        ) {
            checkoutBootstrap({
                test: useStripeTestMode,
            }).then((checkoutBootstrapResult) => {
                const cur = resumeCheckoutSessionLatest.current;
                const {
                    checkoutSession: mostRecentCheckoutSession,
                    paymentIntent,
                } = checkoutBootstrapResult;
                if (
                    mostRecentCheckoutSession
                    && paymentIntent
                ) {
                    // Resume checkout session
                    let sameCart = true;
                    let physicalIds = getPhysicalIdsFromPaymentIntent(paymentIntent);
                    if (cur.userCartItems) {
                        sameCart = isSameCart(
                            cur.userCartItems,
                            mostRecentCheckoutSession.carts[mostRecentCheckoutSession.carts.length - 1].items,
                        );
                    } else {
                        sameCart = false;
                    }

                    if (!sameCart && cur.userCartItems) {
                        const newCart: ICart = {
                            id: uuidv4(),
                            timestamp: Date.now(),
                            items: [...cur.userCartItems],
                        };
                        setAppliedPromotions([]);
                        // Update payment intent cart items
                        stripeUpdatePaymentIntent({
                            test: useStripeTestMode,
                            paymentIntentId: mostRecentCheckoutSession.paymentIntentId,
                            updateMode: STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.final,
                            cartItems: [...cur.userCartItems],
                            // clear promotions if not the same cart
                            promotionIds: [],
                        }).then((updatedPaymentIntent) => {
                            if (
                                updatedPaymentIntent.metadata?.[
                                    CHECKOUT_UPDATE_SKIPPED_STATUS_NOT_MUTABLE_METADATA_KEY
                                ] === 'true'
                            ) {
                                reconcileImmutableStatusSkippedUpdate(updatedPaymentIntent.id);
                                return;
                            }
                            syncOrderCostFromPaymentIntent(updatedPaymentIntent);
                        }).catch((error) => {
                            reportCheckoutPaymentIntentUpdateError(
                                `Unable to update payment intent while resuming checkout: ${
                                    (error as Error)?.message || String(error)
                                }`,
                            );
                        });
                        // Update physical ids only if store items are available.
                        // Otherwise, keep the payment-intent-derived value and let downstream
                        // store hydration update physical item state.
                        if (storeItemByPrice.size > 0) {
                            physicalIds = cur.userCartItems.filter((item) => {
                                const storeItem = storeItemByPrice.get(item.saleNegotiationPriceId || item.priceId);
                                if (storeItem) return storeItem.physical;
                                return false;
                            }).map((item) => item.priceId);
                        }

                        // Update checkout session cart history and status
                        updateCheckoutSessionItem({
                            test: useStripeTestMode,
                            paymentIntentId: mostRecentCheckoutSession.paymentIntentId,
                            statusesToAdd: [{
                                timestamp: Date.now(),
                                value: CHECKOUT_SESSION_STATUS_TYPE.resumed,
                            }],
                            cartsToAdd: [newCart],
                        });
                    } else {
                        updateCheckoutSessionItem({
                            test: useStripeTestMode,
                            paymentIntentId: mostRecentCheckoutSession.paymentIntentId,
                            // only update checkout status
                            statusesToAdd: [{
                                timestamp: Date.now(),
                                value: CHECKOUT_SESSION_STATUS_TYPE.resumed,
                            }],
                        });
                    }

                    const promotions: Stripe.Coupon[] = Object.values(
                        checkoutBootstrapResult.promotions || {},
                    );
                    const serverPromotionIds: string[] = promotions.map((promotion) => promotion.id);
                    const serverPromotionIdSet = new Set(serverPromotionIds);
                    let promotionIdsToAdd: string[] = [];
                    const promotionIdsToRemove: string[] = [];
                    if (mostRecentCheckoutSession.promotionIds) {
                        mostRecentCheckoutSession.promotionIds.forEach((id) => {
                            if (!serverPromotionIdSet.has(id)) {
                                promotionIdsToRemove.push(id);
                            }
                        });
                        const firestorePromotionIdSet = new Set(mostRecentCheckoutSession.promotionIds);
                        serverPromotionIds.forEach((id) => {
                            if (!firestorePromotionIdSet.has(id)) {
                                promotionIdsToAdd.push(id);
                            }
                        });
                    } else if (serverPromotionIds.length > 0) {
                        promotionIdsToAdd = [
                            ...serverPromotionIds,
                        ];
                    }
                    updateCheckoutSessionItem({
                        test: useStripeTestMode,
                        paymentIntentId: mostRecentCheckoutSession.paymentIntentId,
                        promotionIdsToAdd: sameCart && promotionIdsToAdd.length > 0
                            ? promotionIdsToAdd
                            : undefined,
                        promotionIdsToRemove: sameCart && promotionIdsToRemove.length > 0
                            ? promotionIdsToRemove
                            : undefined,
                    });
                    // Restore the server-owned checkout pricing only when the
                    // resumed session still matches the user's current cart.
                    if (sameCart) {
                        if (serverPromotionIds.length > 0) {
                            setAppliedPromotions(promotions);
                        }
                        syncOrderCostFromPaymentIntent(paymentIntent);
                    }
                    setCurrentCheckoutSessionId(mostRecentCheckoutSession.paymentIntentId);
                    setPhysicalItemsInCart(physicalIds);
                    if (physicalIds.length === 0) {
                        setSelectedBillingAddressType({
                            title: 'Use a different billing address',
                            value: BILLING_ADDRESS_TYPE.different,
                        });
                    }
                    setStripeClientSecret(paymentIntent.client_secret || undefined);
                    setStripePaymentIntentId(mostRecentCheckoutSession.paymentIntentId);
                } else if (!attemptedResumingPreviousCheckoutSession) {
                    setAttemptedResumingPreviousCheckoutSession(true);
                }
            }).catch(() => {
                // Fast fallback: if resume lookup fails, move forward with new checkout creation.
                if (currentSessionId) {
                    const message = 'Unable to resume previous checkout session; creating a new one.';
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: message,
                        },
                    });
                }
                setAttemptedResumingPreviousCheckoutSession(true);
            });
        } else if (currentSessionId) {
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.error,
                sessionId: currentSessionId,
                payload: {
                    error: SNACKBAR_MESSAGE_RESUME_CHECKOUT_SESSION_ERROR,
                },
            });
        }
    };

    // Monitor post-payment processing intents so users don't wait forever if they never settle.
    useInterval(() => {
        if (!postPaymentProcessingIntentId) {
            return;
        }
        stripeGetPaymentIntent({
            test: useStripeTestMode,
            paymentIntentId: postPaymentProcessingIntentId,
        }).then((paymentIntent) => {
            if (paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.succeeded) {
                setPostPaymentProcessingIntentId(undefined);
                postPaymentProcessingStartedAtRef.current = null;
                postPaymentProcessingTimeoutNotifiedRef.current = false;
                restoreCheckoutSessionAfterPayment(paymentIntent.id);
                return;
            }

            if (paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.processing) {
                const startedAt = postPaymentProcessingStartedAtRef.current || Date.now();
                if (!postPaymentProcessingStartedAtRef.current) {
                    postPaymentProcessingStartedAtRef.current = startedAt;
                }
                const elapsed = Date.now() - startedAt;
                if (
                    elapsed >= POST_PAYMENT_PROCESSING_MAX_WAIT_DURATION
                    && !postPaymentProcessingTimeoutNotifiedRef.current
                ) {
                    postPaymentProcessingTimeoutNotifiedRef.current = true;
                    setListenForSaleItem(false);
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_SUBMIT_PAYMENT_PROCESSING_TIMEOUT,
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    if (currentSessionId) {
                        recordUserAction({
                            test: useStripeTestMode,
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                error: SNACKBAR_MESSAGE_SUBMIT_PAYMENT_PROCESSING_TIMEOUT,
                            },
                        });
                    }
                }
                return;
            }

            setPostPaymentProcessingIntentId(undefined);
            postPaymentProcessingStartedAtRef.current = null;
            postPaymentProcessingTimeoutNotifiedRef.current = false;
            setListenForSaleItem(false);
            setStripePaymentSucceeded(false);
            let statusToWrite = CHECKOUT_SESSION_STATUS_TYPE.unknownPaymentError;
            if (paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.requiresPaymentMethod) {
                statusToWrite = CHECKOUT_SESSION_STATUS_TYPE.requiresPaymentMethod;
            } else if (paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.requiresAction) {
                statusToWrite = CHECKOUT_SESSION_STATUS_TYPE.requiresAction;
            } else if (paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.requiresConfirmation) {
                statusToWrite = CHECKOUT_SESSION_STATUS_TYPE.requiresConfirmation;
            } else if (paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.requiresCapture) {
                statusToWrite = CHECKOUT_SESSION_STATUS_TYPE.requiresCapture;
            } else if (paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.canceled) {
                statusToWrite = CHECKOUT_SESSION_STATUS_TYPE.abandoned;
            }
            changeCheckoutSessionStatus(statusToWrite);
            const message = `Payment is no longer processing (status: ${paymentIntent.status}). Please try again.`;
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: message,
                icon: PixelCartIcon,
                hasFailure: true,
            });
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: message,
                    },
                });
            }
        }).catch((error) => {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Unable to refresh processing payment status: ${(error as Error)?.message || String(error)}`,
                    },
                });
            }
        });
    }, (
        postPaymentProcessingIntentId
        && !orderSaleItem
    )
        ? POST_PAYMENT_PROCESSING_POLL_INTERVAL_DURATION
        : null);

    const createPaymentIntent = (): void => {
        if (
            user
            && userCartItems
            && userCartItems.length > 0
            && attemptedResumingPreviousCheckoutSession
            && attemptedRestoringCheckoutSessionAfterPayment
            && !currentCheckoutSessionId
            && !stripePaymentIntentId
        ) {
            try {
                stripeCreatePaymentIntentWithCheckoutSession({
                    test: useStripeTestMode,
                    cartItems: userCartItems,
                    userEmail: userEmail || auth.currentUser?.email || undefined,
                    ...(stripeCustomerId ? { customerId: stripeCustomerId } : {}),
                }).then(({ paymentIntent, checkoutSession }) => {
                    const physicalIds = getPhysicalIdsFromPaymentIntent(paymentIntent);
                    setCurrentCheckoutSessionId(checkoutSession.paymentIntentId);
                    setPhysicalItemsInCart(physicalIds);
                    if (physicalIds.length === 0) {
                        setSelectedBillingAddressType({
                            title: 'Use a different billing address',
                            value: BILLING_ADDRESS_TYPE.different,
                        });
                    }
                    setStripeClientSecret(paymentIntent.client_secret || undefined);
                    setStripePaymentIntentId(paymentIntent.id);
                    syncOrderCostFromPaymentIntent(paymentIntent);
                }).catch((e) => {
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_STRIPE_PAYMENT_INTENT_CHECKOUT_SESSION_GENERAL_ERROR((e as any).toString()),
                        icon: PixelCartIcon,
                        hasFailure: true,
                    });
                    if (currentSessionId) {
                        recordUserAction({
                            test: useStripeTestMode,
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                error: SNACKBAR_MESSAGE_STRIPE_PAYMENT_INTENT_CHECKOUT_SESSION_GENERAL_ERROR((e as any).toString()),
                            },
                        });
                    }
                });
            } catch (e) {
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_STRIPE_PAYMENT_INTENT_CHECKOUT_SESSION_GENERAL_ERROR((e as any).toString()),
                    icon: PixelCartIcon,
                    hasFailure: true,
                });
                if (currentSessionId) {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: SNACKBAR_MESSAGE_STRIPE_PAYMENT_INTENT_CHECKOUT_SESSION_GENERAL_ERROR((e as any).toString()),
                        },
                    });
                }
            }
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_STRIPE_PAYMENT_INTENT_USER_ERROR,
                icon: PixelCartIcon,
                hasFailure: true,
            });
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: SNACKBAR_MESSAGE_STRIPE_PAYMENT_INTENT_USER_ERROR,
                    },
                });
            }
        }
    };

    const determineBookDiscountEligibility = async (): Promise<void> => {
        if (
            user
            && userCartItems
            && userCartItems.length > 0
            && stripePaymentIntentId
        ) {
            const test = useStripeTestMode;
            let promotionApplied = false;
            let updatedPaymentIntent: Stripe.PaymentIntent | null | undefined;
            if (
                purchasedWebBookSessionIds.length > 0
                && (
                    purchasedDigitalBookSessionIds.length === 0 // We only allow to reedem once
                    && !!userCartItems.find((item: ICartItem) => (
                        item.productId === DIGITAL_BOOK_PRODUCT_ID
                        || item.productId === DIGITAL_BOOK_PREORDER_PRODUCT_ID
                    ))
                )
                && purchasedPhysicalBookSessionIds.length === 0 // We only allow to reedem once
            ) {
                try {
                    updatedPaymentIntent = await stripeApplyWebBookDiscount({
                        test,
                        salePaymentIntentId: purchasedWebBookSessionIds[0],
                        currPaymentIntentId: stripePaymentIntentId,
                    });
                    if (
                        updatedPaymentIntent?.metadata?.[
                            CHECKOUT_UPDATE_SKIPPED_STATUS_NOT_MUTABLE_METADATA_KEY
                        ] === 'true'
                    ) {
                        reconcileImmutableStatusSkippedUpdate(updatedPaymentIntent.id);
                        return;
                    }
                    if (updatedPaymentIntent != null) promotionApplied = true;
                } catch (error) {
                    reportCheckoutPaymentIntentUpdateError(
                        `Unable to apply Web Book discount: ${
                            (error as Error)?.message || String(error)
                        }`,
                    );
                }
            } else {
                // Either no physical book or digital book in customer cart
                // or customer has not bought a previous book that makes them eligible for a discount
            }

            let updatedPromotions: Stripe.Coupon[] | undefined;
            if (promotionApplied) {
                const promotionMap = await stripeGetPromotions({
                    test: useStripeTestMode,
                    paymentIntentId: stripePaymentIntentId,
                });
                if (promotionMap.size > 0) {
                    // Change locally
                    updatedPromotions = Array.from(promotionMap.values());
                    const serverPromotionIds: string[] = updatedPromotions.map((promotion) => promotion.id);
                    setAppliedPromotions(updatedPromotions);
                    // Change in firestore
                    const session = await getCheckoutSessionItem({
                        test: useStripeTestMode,
                        paymentIntentId: stripePaymentIntentId,
                    });
                    const serverPromotionIdSet = new Set(serverPromotionIds);
                    let promotionIdsToAdd: string[] = [];
                    const promotionIdsToRemove: string[] = [];
                    if (session.promotionIds) {
                        session.promotionIds.forEach((id) => {
                            if (!serverPromotionIdSet.has(id)) {
                                promotionIdsToRemove.push(id);
                            }
                        });
                        const firestorePromotionIdSet = new Set(session.promotionIds);
                        serverPromotionIds.forEach((id) => {
                            if (!firestorePromotionIdSet.has(id)) {
                                promotionIdsToAdd.push(id);
                            }
                        });
                    } else if (serverPromotionIds.length > 0) {
                        promotionIdsToAdd = [
                            ...serverPromotionIds,
                        ];
                    }
                    updateCheckoutSessionItem({
                        test: useStripeTestMode,
                        paymentIntentId: stripePaymentIntentId,
                        promotionIdsToAdd: promotionIdsToAdd.length > 0
                            ? promotionIdsToAdd
                            : undefined,
                        promotionIdsToRemove: promotionIdsToRemove.length > 0
                            ? promotionIdsToRemove
                            : undefined,
                    });
                }
            } else {
                // No promotions applied
            }

            if (updatedPaymentIntent) {
                await syncOrderCostFromPaymentIntent(updatedPaymentIntent);
            }
        }
    };

    const onPromotionCodeToggleButtonEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.promotionCodeToggleButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onPromotionCodeToggleButtonLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onPromotionCodeToggleButtonClick = (): void => {
        setPromotionCodeInputIsVisible(!promotionCodeInputIsVisible);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.togglePromotionCode,
                sessionId: currentSessionId,
                payload: {
                    expanded: !promotionCodeInputIsVisible,
                },
            });
        }
    };

    const onPromotionCodeInputEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.input,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onPromotionCodeInputLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onPromotionCodeInputFocus = (): void => {
        setInputFocused(true);

        // Play Sound
        if (hasSound && buttonClickClip.current) {
            buttonClickClip.current.pause();
            buttonClickClip.current.currentTime = 0;
            playAudio(buttonClickClip.current);
        }
    };

    const onPromotionCodeInputBlur = (): void => {
        setInputFocused(false);
    };

    const onPromotionCodeInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const normalizedCode = normalizePromotionCodeForValidation(e.target.value);
        if (promotionCodeInvalid) setPromotionCodeInvalid(false);
        e.target.value = normalizedCode;
        setPromotionCode(normalizedCode);
    };

    /**
     * Reads the uncontrolled promotion input and keeps the DOM value in sync
     * with the normalized code we validate against.
     */
    const getNormalizedPromotionCodeInputValue = (): string => {
        const normalizedCode = normalizePromotionCodeForValidation(
            promotionCodeInputRef.current?.value || '',
        );
        if (promotionCodeInputRef.current) {
            promotionCodeInputRef.current.value = normalizedCode;
        }
        return normalizedCode;
    };

    /**
     * Manages tracking enter key presses during changes to promotion code input
     * @param e keyboard event
     */
    const checkForEnter = (e: React.KeyboardEvent): void => {
        if (e.key === KEYCODE.enter) {
            // Cancel the default action, if needed
            e.preventDefault();

            // Promotion code field is focused
            // Attempt to apply code
            if (document.activeElement === promotionCodeInputRef.current) {
                // Note that ref.current may be null. This is expected, because you may
                // conditionally render the ref-ed element, or you may forgot to assign it
                if (!promotionCodeInputRef.current) {
                    if (currentSessionId) {
                        recordUserAction({
                            test: useStripeTestMode,
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                // eslint-disable-next-line max-len
                                error: UNASSIGNED_ERROR_MESSAGE(PROMOTION_CODE_INPUT_REF_NAME),
                            },
                        });
                    }
                    throw Error(UNASSIGNED_ERROR_MESSAGE(PROMOTION_CODE_INPUT_REF_NAME));
                }
                const code = getNormalizedPromotionCodeInputValue();
                onPromotionCodeApply(code);
                if (currentSessionId) {
                    // Record user action
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.applyPromotionCode,
                        sessionId: currentSessionId,
                        payload: {
                            code,
                        },
                    });
                }
            }
        } else if (e.key === KEYCODE.escape) {
            // Cancel the default action, if needed
            e.preventDefault();

            // Note that ref.current may be null. This is expected, because you may
            // conditionally render the ref-ed element, or you may forgot to assign it
            if (!promotionCodeInputRef.current) {
                if (currentSessionId) {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: UNASSIGNED_ERROR_MESSAGE(PROMOTION_CODE_INPUT_REF_NAME),
                        },
                    });
                }
                throw Error(UNASSIGNED_ERROR_MESSAGE(PROMOTION_CODE_INPUT_REF_NAME));
            }

            // Blur Input
            promotionCodeInputRef.current.blur();
        }
    };

    const onPromotionCodeAddButtonEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.promotionCodeAddButton,
            [promotionCodeInvalid ? CURSOR_SIGN.caution : CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onPromotionCodeAddButtonLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onPromotionCodeAddButtonClick = (): void => {
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!promotionCodeInputRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(PROMOTION_CODE_INPUT_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(PROMOTION_CODE_INPUT_REF_NAME));
        }
        const code = getNormalizedPromotionCodeInputValue();
        onPromotionCodeApply(code);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.applyPromotionCode,
                sessionId: currentSessionId,
                payload: {
                    code,
                },
            });
        }
    };

    const onPromotionCodeApplyLatest = useLatest({
        promotionCodeInvalid,
    });
    const onPromotionCodeApply = (code: string): void => {
        const normalizedCode = normalizePromotionCodeForValidation(code);
        // This handler is used by both manual entry and URL auto-apply. The input
        // ref can be absent when the promotion UI is collapsed, so do not hard-fail.
        if (promotionCodeInputRef.current) {
            promotionCodeInputRef.current.value = '';
        }
        setPromotionCode(normalizedCode);

        if (!stripePaymentIntentId) {
            setIsValidatingPromotionCode(false);
            return;
        }
        setIsValidatingPromotionCode(true);
        stripeApplyPromotionCode({
            previewNegotiatedPrice: params.checkpoint !== CHECKOUT_CHECKPOINT.review,
            test: useStripeTestMode,
            paymentIntentId: stripePaymentIntentId,
            promotionCode: normalizedCode,
        }).then((applyPromotionResult: IStripeApplyPromotionCodeResult | null) => {
            const cur = onPromotionCodeApplyLatest.current;
            if (
                applyPromotionResult
                && applyPromotionResult.status === STRIPE_PROMOTION_VALIDATION_STATUS.valid
                && applyPromotionResult.type === STRIPE_PROMOTION_VALID_ITEM_TYPE.coupon
                && applyPromotionResult.paymentIntent
                && applyPromotionResult.promotions
            ) {
                if (cur.promotionCodeInvalid) setPromotionCodeInvalid(false);
                setPromotionCode('');
                const updatedPaymentIntent = applyPromotionResult.paymentIntent;
                const updatedPromotions = Object.values(applyPromotionResult.promotions);
                if (
                    updatedPaymentIntent.metadata?.[
                        CHECKOUT_UPDATE_SKIPPED_STATUS_NOT_MUTABLE_METADATA_KEY
                    ] === 'true'
                ) {
                    setIsValidatingPromotionCode(false);
                    reconcileImmutableStatusSkippedUpdate(updatedPaymentIntent.id);
                    return;
                }
                syncOrderCostFromPaymentIntent(updatedPaymentIntent).finally(() => {
                    setIsValidatingPromotionCode(false);
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_APPLY_PROMOTION_CODE_SUCCESS(normalizedCode),
                        icon: BoxIcon,
                        hasSuccess: true,
                    });
                    if (hasSound && successClip.current) {
                        successClip.current.pause();
                        successClip.current.currentTime = 0;
                        playAudio(successClip.current);
                    }
                    const coupon = updatedPromotions.find((promotion) => normalizePromotionCodeForValidation(promotion.name || '') === normalizedCode);
                    if (coupon) manualCouponCodeRef.current = { code: normalizedCode, id: coupon.id };
                    setAppliedPromotions(updatedPromotions);
                });
            } else if (
                applyPromotionResult
                && applyPromotionResult.status === STRIPE_PROMOTION_VALIDATION_STATUS.valid
                && applyPromotionResult.type === STRIPE_PROMOTION_VALID_ITEM_TYPE.price
                && applyPromotionResult.price
            ) {
                if (cur.promotionCodeInvalid) setPromotionCodeInvalid(false);
                if (params.checkpoint !== CHECKOUT_CHECKPOINT.review) {
                    setNegotiationPreview({ code: normalizedCode, price: applyPromotionResult.price });
                    setPromotionCode('');
                    setIsValidatingPromotionCode(false);
                } else {
                    onSubmitPromotionCode({
                        type: STRIPE_PROMOTION_VALID_ITEM_TYPE.price,
                        price: applyPromotionResult.price,
                    }, normalizedCode);
                }
            } else {
                const validationErrorMessage = (
                    applyPromotionResult?.status === STRIPE_PROMOTION_VALIDATION_STATUS.expired
                        ? SNACKBAR_MESSAGE_EXPIRED_PROMOTION_CODE_ERROR(normalizedCode)
                        : SNACKBAR_MESSAGE_INVALID_PROMOTION_CODE_ERROR(normalizedCode)
                );
                setIsValidatingPromotionCode(false);
                if (!cur.promotionCodeInvalid) setPromotionCodeInvalid(true);
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: validationErrorMessage,
                    icon: PixelCartIcon,
                    hasFailure: true,
                    failureTelemetryType: FAILURE_TELEMETRY_TYPE.userCorrectable,
                });
                if (currentSessionId) {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: validationErrorMessage,
                            failureTelemetryType: FAILURE_TELEMETRY_TYPE.userCorrectable,
                        },
                    });
                }
            }
        }).catch((error) => {
            reportCheckoutPaymentIntentUpdateError(
                `Unable to apply promotion code: ${
                    (error as Error)?.message || String(error)
                }`,
            );
        });
    };

    useEffect(() => {
        const codeFromLink = `${searchParams.get('promotionCode') || ''}`;
        const normalizedCodeFromLink = normalizePromotionCodeForValidation(codeFromLink);
        if (
            !user
            || !stripePaymentIntentId
            || !normalizedCodeFromLink
            || autoAppliedPromotionCodeRef.current === codeFromLink
        ) {
            return;
        }
        autoAppliedPromotionCodeRef.current = codeFromLink;
        onPromotionCodeApply(codeFromLink);

        // Remove the param after applying once to avoid accidental re-applies on refresh.
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('promotionCode');
        setSearchParams(nextSearchParams, { replace: true });
    }, [
        user,
        stripePaymentIntentId,
        searchParams,
    ]);

    const onSubmitPromotionCode = async (
        code: IStripePromotionValidItem,
        submittedCode = promotionCode,
    ): Promise<void> => {
        const cachedCode = submittedCode;
        if (
            code.type === STRIPE_PROMOTION_VALID_ITEM_TYPE.price
            && code.price
            && stripePaymentIntentId
            && userCartItems
        ) {
            // Change locally
            setPromotionCode('');

            // Update user cart
            const newCart: ICartItem[] = [];
            const product = code.price.product as Stripe.Product;
            userCartItems.forEach((item: ICartItem) => {
                if (item.productId === product.id) {
                    newCart.push({
                        ...item,
                        updated: Date.now(),
                        saleNegotiationPriceId: code.price!.id,
                    });
                } else {
                    newCart.push(item);
                }
            });
            updateUser({
                test: useStripeTestMode,
                cart: newCart,
            });

            // Update cart history in checkout session
            updateCheckoutSessionItem({
                test: useStripeTestMode,
                paymentIntentId: stripePaymentIntentId,
                cartsToAdd: [{
                    id: uuidv4(),
                    timestamp: Date.now(),
                    items: [...newCart],
                }],
            });

            // Update payment intent cart items
            stripeUpdatePaymentIntent({
                test: useStripeTestMode,
                paymentIntentId: stripePaymentIntentId,
                updateMode: STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.final,
                cartItems: [...newCart],
            }).then((updatedPaymentIntent) => {
                if (
                    updatedPaymentIntent.metadata?.[
                        CHECKOUT_UPDATE_SKIPPED_STATUS_NOT_MUTABLE_METADATA_KEY
                    ] === 'true'
                ) {
                    setIsValidatingPromotionCode(false);
                    reconcileImmutableStatusSkippedUpdate(updatedPaymentIntent.id);
                    return;
                }
                syncOrderCostFromPaymentIntent(updatedPaymentIntent).finally(() => {
                    setIsValidatingPromotionCode(false);
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_APPLY_PROMOTION_CODE_SUCCESS(cachedCode),
                        icon: BoxIcon,
                        hasSuccess: true,
                    });
                    if (hasSound && successClip.current) {
                        successClip.current.pause();
                        successClip.current.currentTime = 0;
                        playAudio(successClip.current);
                    }
                });
            });
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_APPLY_PROMOTION_CODE_ERROR(cachedCode),
                icon: PixelCartIcon,
                hasFailure: true,
            });
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: SNACKBAR_MESSAGE_APPLY_PROMOTION_CODE_ERROR(cachedCode),
                    },
                });
            }
        }
    };

    useEffect(() => {
        // A manually entered review URL cannot skip resolution of a local preview.
        if (negotiationPreview && params.checkpoint === CHECKOUT_CHECKPOINT.review) {
            navigate(`/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.billing}`, { replace: true });
        }
    }, [negotiationPreview, params.checkpoint, navigate]);

    /** Checks offers at the billing boundary; transient errors leave checkout on billing. */
    const preparePromotionsForReview = async (email: string): Promise<ICheckoutPromotionReviewResult> => {
        if (!stripePaymentIntentId) throw new Error('Checkout is not ready.');
        if (!negotiationPreview && !manualCouponCodeRef.current
            && !userCartItems?.some((item) => item.saleNegotiationPriceId)) {
            return { cartItems: userCartItems || [], promotionRemoved: false, promotionsChecked: false };
        }
        const previewProduct = negotiationPreview?.price.product as Stripe.Product | undefined;
        const review = await resolveCheckoutPromotionReview({
            cartItems: userCartItems || [],
            preview: negotiationPreview && previewProduct ? {
                code: negotiationPreview.code,
                priceId: negotiationPreview.price.id,
                productId: previewProduct.id,
            } : undefined,
            validate: async (code) => {
                const result = await stripeApplyPromotionCode({
                    test: useStripeTestMode,
                    paymentIntentId: stripePaymentIntentId,
                    promotionCode: code,
                    userEmail: email,
                });
                return result?.status === STRIPE_PROMOTION_VALIDATION_STATUS.valid
                    && result.type === STRIPE_PROMOTION_VALID_ITEM_TYPE.price;
            },
        });
        const coupon = manualCouponCodeRef.current;
        if (coupon) {
            const result = await stripeApplyPromotionCode({
                test: useStripeTestMode,
                paymentIntentId: stripePaymentIntentId,
                promotionCode: coupon.code,
                userEmail: email,
            });
            if (result?.status !== STRIPE_PROMOTION_VALIDATION_STATUS.valid) {
                review.promotionRemoved = true;
                review.promotionIds = appliedPromotions.filter((item) => item.id !== coupon.id).map((item) => item.id);
            }
        }
        review.promotionsChecked = true;
        return review;
    };

    /** Commits the validated cart locally only after the payable total has been updated. */
    const finishPromotionsForReview = async (payment: Stripe.PaymentIntent): Promise<void> => {
        const cart = JSON.parse(payment.metadata.cartItems || '[]') as ICartItem[];
        await updateUser({ test: useStripeTestMode, cart });
        setNegotiationPreview(undefined);
        const promotions = await stripeGetPromotions({ test: useStripeTestMode, paymentIntentId: payment.id });
        setAppliedPromotions(Array.from(promotions.values()));
        if (manualCouponCodeRef.current && !promotions.has(manualCouponCodeRef.current.id)) {
            manualCouponCodeRef.current = undefined;
        }
        await syncOrderCostFromPaymentIntent(payment);
        const catalog = await stripeGetStoreItems({
            test: useStripeTestMode,
            paymentIntentId: payment.id,
            negotiationRequestPriceIds: cart.map((item) => item.saleNegotiationPriceId).filter((id): id is string => !!id),
        });
        setStoreItemByPrice(catalog);
    };

    const onPromotionCodeBadgeRemoveButtonEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.promotionCodeBadgeRemoveButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onPromotionCodeBadgeRemoveButtonLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onPromotionCodeBadgeRemoveButtonClick = (
        promotionId: string,
        type: STRIPE_PROMOTION_VALID_ITEM_TYPE,
        e: React.MouseEvent | React.TouchEvent,
    ): void => {
        onCursorLeave(e);
        if (negotiationPreview?.price.id === promotionId) {
            setNegotiationPreview(undefined);
            return;
        }

        if (
            stripePaymentIntentId
            && type === STRIPE_PROMOTION_VALID_ITEM_TYPE.coupon
        ) {
            // Change locally
            const updatedPromotions = appliedPromotions.filter((promotion) => promotion.id !== promotionId);
            setAppliedPromotions([...updatedPromotions]);
            const promotionIds = [...updatedPromotions.map(((promotion) => promotion.id))];

            // Change in firestore
            updateCheckoutSessionItem({
                test: useStripeTestMode,
                paymentIntentId: stripePaymentIntentId,
                promotionIdsToRemove: [promotionId],
            });

            // Change in stripe
            stripeUpdatePaymentIntent({
                test: useStripeTestMode,
                paymentIntentId: stripePaymentIntentId,
                updateMode: STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.final,
                promotionIds,
            }).then((updatedPaymentIntent) => {
                if (
                    updatedPaymentIntent.metadata?.[
                        CHECKOUT_UPDATE_SKIPPED_STATUS_NOT_MUTABLE_METADATA_KEY
                    ] === 'true'
                ) {
                    reconcileImmutableStatusSkippedUpdate(updatedPaymentIntent.id);
                    return;
                }
                syncOrderCostFromPaymentIntent(updatedPaymentIntent);
            }).catch((error) => {
                reportCheckoutPaymentIntentUpdateError(
                    `Unable to remove promotion from payment intent: ${
                        (error as Error)?.message || String(error)
                    }`,
                );
            });
        } else if (
            userCartItems
            && stripePaymentIntentId
            && type === STRIPE_PROMOTION_VALID_ITEM_TYPE.price
        ) {
            // Update user cart
            const newCart: ICartItem[] = [];
            userCartItems.forEach((item: ICartItem) => {
                if (
                    item.saleNegotiationPriceId
                    && item.saleNegotiationPriceId === promotionId
                ) {
                    newCart.push({
                        priceId: item.priceId,
                        productId: item.productId,
                        timestamp: item.timestamp,
                        updated: Date.now(),
                        quantity: item.quantity,
                    });
                } else {
                    newCart.push(item);
                }
            });
            updateUser({
                test: useStripeTestMode,
                cart: newCart,
            });

            // Update cart history
            updateCheckoutSessionItem({
                test: useStripeTestMode,
                paymentIntentId: stripePaymentIntentId,
                cartsToAdd: [{
                    id: uuidv4(),
                    timestamp: Date.now(),
                    items: [...newCart],
                }],
            });

            // Update payment intent cart items
            stripeUpdatePaymentIntent({
                test: useStripeTestMode,
                paymentIntentId: stripePaymentIntentId,
                updateMode: STRIPE_PAYMENT_INTENT_UPDATE_MODE_TYPE.final,
                cartItems: [...newCart],
            }).then((updatedPaymentIntent) => {
                if (
                    updatedPaymentIntent.metadata?.[
                        CHECKOUT_UPDATE_SKIPPED_STATUS_NOT_MUTABLE_METADATA_KEY
                    ] === 'true'
                ) {
                    reconcileImmutableStatusSkippedUpdate(updatedPaymentIntent.id);
                    return;
                }
                syncOrderCostFromPaymentIntent(updatedPaymentIntent);
            }).catch((error) => {
                reportCheckoutPaymentIntentUpdateError(
                    `Unable to update payment intent cart after removing promotion code: ${
                        (error as Error)?.message || String(error)
                    }`,
                );
            });
        }

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.removePromotionCodeBadge,
                sessionId: currentSessionId,
                payload: {
                    promotionId,
                    type,
                },
            });
        }

        // Play Sound
        if (hasSound && deleteClip.current) {
            deleteClip.current.pause();
            deleteClip.current.currentTime = 0;
            playAudio(deleteClip.current);
        }
    };

    const onOrderSummaryToggleButtonEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.orderSummaryToggleButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onOrderSummaryToggleButtonLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onOrderSummaryToggleButtonClick = (): void => {
        setOrderSummaryIsVisible(!orderSummaryIsVisible);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.toggleOrderSummary,
                sessionId: currentSessionId,
                payload: {
                    expanded: !orderSummaryIsVisible,
                },
            });
        }
    };

    const checkForTaxTransaction = (): void => {
        if (stripePaymentIntentId) {
            stripeGetPaymentIntent({
                test: useStripeTestMode,
                paymentIntentId: stripePaymentIntentId,
            }).then((paymentIntent) => {
                if (!paymentIntent) {
                    requestTaxResolutionPollingCycle();
                } else {
                    resolveOrderCostFromTaxCalculation(paymentIntent).then((resolvedOrderCost) => {
                        if (!resolvedOrderCost) {
                            requestTaxResolutionPollingCycle();
                        } else {
                            setTaxResolution((cur) => ({
                                phase: TAX_RESOLUTION_PHASE.resolved,
                                cycle: cur.cycle,
                            }));
                        }
                    });
                }
            });
        }
    };

    // ===== Side Effects =====

    /**
     * Manages page title changes
     */
    useEffect(() => {
        updatePageTitle(
            'Checkout',
        );
    }, []);

    /**
     * Records View Checkout Page User Action
     */
    useEffect(() => {
        if (
            !currentSessionId
            || recordedViewPageUserActionKey.current === location.key
        ) return;
        recordCheckoutPageUserAction();
        recordedViewPageUserActionKey.current = location.key;
    }, [
        currentSessionId,
        location.key,
    ]);

    /**
     * Redirects user if they're on a page they shouldn't be on
     */
    useEffect(() => {
        if (
            params.checkpoint === CHECKOUT_CHECKPOINT.review
            && !stripePaymentSucceeded
            && !currentCheckoutSessionId
        ) {
            const fallbackCheckpoint = physicalItemsInCart.length > 0
                ? CHECKOUT_CHECKPOINT.info
                : CHECKOUT_CHECKPOINT.billing;
            navigate(
                `/${PAGE_ROUTE.checkout}/${fallbackCheckpoint}`,
                {
                    state: {
                        prevPath: location.pathname,
                    },
                },
            );
            return;
        }
        if (
            user
            && !!stripePaymentIntentId // ensures checkout session and payment intent estabished
            && !stripePaymentSucceeded
        ) {
            if (
                // No checkpoint
                !params.checkpoint
                // Invalid checkpoint
                || (
                    params.checkpoint !== CHECKOUT_CHECKPOINT.info
                    && params.checkpoint !== CHECKOUT_CHECKPOINT.shipping
                    && params.checkpoint !== CHECKOUT_CHECKPOINT.billing
                    && params.checkpoint !== CHECKOUT_CHECKPOINT.review
                    && params.checkpoint !== CHECKOUT_CHECKPOINT.confirmation
                )
            ) {
                navigate(
                    `/${PAGE_ROUTE.cart}`,
                    {
                        state: {
                            prevPath: location.pathname,
                        },
                    },
                );
            } else if (
                params.checkpoint === CHECKOUT_CHECKPOINT.shipping
                && physicalItemsInCart.length > 0
                && (
                    !addressIsValid // Address is valid when we load page
                    || customerStreetOne.length === 0
                )
            ) {
                navigate(
                    `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.info}`,
                    {
                        state: {
                            prevPath: location.pathname,
                        },
                    },
                );
            } else if (
                params.checkpoint === CHECKOUT_CHECKPOINT.billing
                && physicalItemsInCart.length > 0
                && !selectedShippingRateID
            ) {
                navigate(
                    `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.shipping}`,
                    {
                        state: {
                            prevPath: location.pathname,
                        },
                    },
                );
            } else if (
                (
                    params.checkpoint === CHECKOUT_CHECKPOINT.info
                    || params.checkpoint === CHECKOUT_CHECKPOINT.shipping
                )
                && physicalItemsInCart.length === 0
            ) {
                navigate(
                    `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.billing}`,
                    {
                        state: {
                            prevPath: location.pathname,
                        },
                    },
                );
            } else if (
                params.checkpoint === CHECKOUT_CHECKPOINT.review
                && (
                    !selectedBillingAddressType
                    || (
                        selectedBillingAddressType.value === BILLING_ADDRESS_TYPE.different
                        && (
                            !billingAddressIsValid
                            || customerBillingStreetOne.length === 0
                        )
                    ) || (
                        physicalItemsInCart.length > 0
                        && !selectedShippingRateID
                    ) || (
                        physicalItemsInCart.length > 0
                        && (
                            !addressIsValid
                            || customerStreetOne.length === 0
                        )
                    )
                )
            ) {
                navigate(
                    `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.billing}`,
                    {
                        state: {
                            prevPath: location.pathname,
                        },
                    },
                );
            } else if (params.checkpoint === CHECKOUT_CHECKPOINT.confirmation) {
                navigate(
                    `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.info}`,
                    {
                        state: {
                            prevPath: location.pathname,
                        },
                    },
                );
            }
        } else if (
            // Empty cart
            user
            && (
                !userCartItems
                || userCartItems.length === 0
            ) && attemptedRestoringCheckoutSessionAfterPayment
            // Allow resume attempt to complete first; only bounce if it failed.
            && attemptedResumingPreviousCheckoutSession
            // Keep user in checkout when a PI already exists; cart may have been cleared
            // while post-payment state is still reconciling.
            && !stripePaymentIntentId
            && !stripePaymentSucceeded
        ) {
            navigate(
                `/${PAGE_ROUTE.cart}`,
                {
                    state: {
                        prevPath: location.pathname,
                    },
                },
            );
        }
    }, [
        user,
        userCartItems,
        params.checkpoint,
        physicalItemsInCart,
        stripePaymentIntentId,
        addressIsValid,
        selectedShippingRateID,
        selectedBillingAddressType,
        billingAddressIsValid,
        customerStreetOne,
        customerBillingStreetOne,
        fetchedStoreItems,
        attemptedRestoringCheckoutSessionAfterPayment,
        attemptedResumingPreviousCheckoutSession,
        currentCheckoutSessionId,
        stripePaymentSucceeded,
    ]);

    /**
     * Manages Checkpoint Placeholder Ellipsis Changes
     */
    useInterval(() => {
        handlePlaceholderEllipsisText();
    }, isRunningCheckpointPlaceholderInterval ? CHECKPOINT_PLACEHOLDER_ELLIPSIS_INTERVAL_DURATION : null);

    /**
     * On extra-small checkout flows, reset scroll to top when moving between checkpoints.
     */
    useEffect(() => {
        if (viewportDimensions.width <= MEDIA_QUERY_SIZE.extraSmall.max) {
            containerRef.current?.scrollTo({
                top: 0,
                behavior: 'auto',
            });
            window.scrollTo(0, 0);
        }
    }, [
        params.checkpoint,
        viewportDimensions.width,
    ]);

    /**
     * Fetches stripe customer id
     */
    useEffect(() => {
        if (
            user
            && !fetchingStripeCustomerId
            && !stripeCustomerId
        ) {
            fetchStripeCustomerId();
        }
    }, [user]);

    /**
     * Fetches and saves list of a user's saved addresses
     */
    useEffect(() => {
        if (user && !fetchingUserAddresses) {
            fetchUserAddresses();
        }
    }, [user]);

    /**
     * Fetches and saves store items
     */
    useEffect(() => {
        if (
            user
            && (
                (
                    (userCartItems && userCartItems.length > 0)
                    || (
                        stripePaymentSucceeded
                        && stripeSucceededPaymentIntent
                        && !!stripeSucceededPaymentIntent.metadata?.cartItems
                    )
                )
                && !fetchingStoreItems
            )
        ) {
            fetchStoreItems();
        }
    }, [
        userCartItems,
        stripePaymentSucceeded,
        stripeSucceededPaymentIntent,
        user,
    ]);

    /**
     * Manages checking whether user has bought a book in the past
     */
    useEffect(() => {
        if (user && !checkedBookPurchases) {
            determineBookPurchases();
        }
    }, [
        user,
        checkedBookPurchases,
    ]);

    /**
     * Manages checking whether user has bought a book in the past
     */
    useEffect(() => {
        if (
            user
            && !checkedPaymentMethods
            && stripeCustomerId
        ) {
            determinePaymentMethods();
        }
    }, [
        user,
        stripeCustomerId,
    ]);

    /**
     * Calculates cart subtotal
     */
    useEffect(() => {
        if (
            user
            && userCartItems
            && userCartItems.length > 0
            && fetchedStoreItems
            && !fetchingStoreItems
            && storeItemByPrice.size > 0
            && !orderCost
            && attemptedRestoringCheckoutSessionAfterPayment
            && !stripePaymentSucceeded
        ) {
            // should always be true if we remain on checkout page
            // if it's empty we are rerouted to the cart page
            calculateCartSubTotal();
        }
    }, [
        user,
        userCartItems,
        fetchingStoreItems,
        fetchedStoreItems,
        storeItemByPrice,
        attemptedRestoringCheckoutSessionAfterPayment,
    ]);

    /**
     * Attempts to restore checkout session after payment redirect
     */
    useEffect(() => {
        const clientSecret = searchParams.get('payment_intent_client_secret');
        const paymentIntentId = searchParams.get('payment_intent');
        if (
            user
            && clientSecret
            && paymentIntentId
        ) {
            // Remove Stripe redirect query parameters from URL.
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.delete('payment_intent');
            nextSearchParams.delete('payment_intent_client_secret');
            nextSearchParams.delete('redirect_status');
            setSearchParams(nextSearchParams, { replace: true });

            // must be before we set stripe payment intent id
            // otherwise will redirect to another page
            setStripePaymentSucceeded(true);
            setStripeClientSecret(clientSecret);
            setStripePaymentIntentId(paymentIntentId);
            restoreCheckoutSessionAfterPayment(paymentIntentId);
        } else if (
            user
            && searchParams
            && !stripeClientSecret
            && !stripePaymentIntentId
            && !attemptedRestoringCheckoutSessionAfterPayment
        ) {
            setAttemptedRestoringCheckoutSessionAfterPayment(true);
        }
    }, [
        user,
        searchParams,
    ]);

    /**
     * Reconcile stale local checkout state with Stripe PI status while on editable checkpoints.
     * This avoids trying to mutate tax data on an already-succeeded payment intent.
     */
    useEffect(() => {
        if (
            !user
            || !stripePaymentIntentId
            || stripePaymentSucceeded
        ) {
            return;
        }
        if (
            params.checkpoint == null
            || params.checkpoint === CHECKOUT_CHECKPOINT.confirmation
        ) {
            return;
        }

        const syncKey = `${stripePaymentIntentId}:${params.checkpoint}`;
        if (postPaymentStateSyncByLocationRef.current === syncKey) {
            return;
        }
        postPaymentStateSyncByLocationRef.current = syncKey;

        stripeGetPaymentIntent({
            test: useStripeTestMode,
            paymentIntentId: stripePaymentIntentId,
        }).then((paymentIntent) => {
            if (
                paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.succeeded
                || paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUS.processing
            ) {
                setStripePaymentSucceeded(true);
                if (!stripeClientSecret && paymentIntent.client_secret) {
                    setStripeClientSecret(paymentIntent.client_secret);
                }
                restoreCheckoutSessionAfterPayment(paymentIntent.id);
                navigate(
                    `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.confirmation}`,
                    {
                        state: {
                            prevPath: location.pathname,
                        },
                    },
                );
            }
        }).catch((error) => {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Unable to reconcile payment intent status before checkout update: ${(error as Error)?.message || String(error)}`,
                    },
                });
            }
        });
    }, [
        user,
        stripePaymentIntentId,
        stripePaymentSucceeded,
        params.checkpoint,
        stripeClientSecret,
        currentSessionId,
    ]);

    /**
     * Attempts to restore checkout session after leaving checkout session
     * Important Note: Succeeds restore checkout session after payment redirect side effect
     */
    useEffect(() => {
        if (
            user
            && typeof userCartItems !== 'undefined'
            && attemptedRestoringCheckoutSessionAfterPayment
            && !attemptedResumingPreviousCheckoutSession
            && !currentCheckoutSessionId
            && !stripePaymentIntentId
            && !stripePaymentSucceeded
        ) {
            resumeCheckoutSession();
        }
    }, [
        user,
        userCartItems,
        attemptedRestoringCheckoutSessionAfterPayment,
        attemptedResumingPreviousCheckoutSession,
        currentCheckoutSessionId,
        stripePaymentIntentId,
        stripePaymentSucceeded,
    ]);

    /**
     * Creates a payment intent if no restored checkout session
     * Important Note: Succeeds restore checkout session after leaving checkout session
     */
    useEffect(() => {
        if (
            user
            && userCartItems
            && userCartItems.length > 0
            && attemptedResumingPreviousCheckoutSession
            && attemptedRestoringCheckoutSessionAfterPayment
            && !currentCheckoutSessionId
            && !stripePaymentIntentId
        ) {
            createPaymentIntent();
        }
        // no need to put user because necessary for this to change
    }, [attemptedResumingPreviousCheckoutSession]);

    /**
     * Determine if eligible for web book discounts
     */
    useEffect(() => {
        if (
            user
            && userCartItems
            && userCartItems.length > 0
            && fetchedStoreItems
            && checkedBookPurchases
            && currentCheckoutSessionId
            && stripePaymentIntentId
            && !stripePaymentSucceeded
        ) {
            determineBookDiscountEligibility();
        }
    }, [
        user,
        fetchedStoreItems,
        stripePaymentIntentId,
        checkedBookPurchases,
        currentCheckoutSessionId,
    ]);

    /**
     * Manages turning off checkpoint placeholder text interval
     */
    useEffect(() => {
        if (
            currentCheckoutSessionId
            && orderCost
            && isRunningCheckpointPlaceholderInterval
        ) {
            setIsRunningCheckpointPlaceholderInterval(false);
        }
    }, [
        currentCheckoutSessionId,
        orderCost,
    ]);

    useEffect(() => {
        if (
            params.checkpoint !== CHECKOUT_CHECKPOINT.confirmation
            || !stripePaymentSucceeded
            || !stripeSucceededPaymentIntent
            || !stripePaymentIntentId
            || !!orderSaleItem
            || fetchingOrderSaleItem
            || listenForSaleItem
        ) {
            return;
        }

        // Confirmation can be reached through multiple routes (redirect restore,
        // immutable-update reconciliation, direct navigation). Ensure we always
        // attempt one immediate sale-item fetch, then enable ping listening.
        fetchSaleOrderItem().then((success) => {
            if (!success) {
                setListenForSaleItem(true);
            }
        });
    }, [
        params.checkpoint,
        stripePaymentSucceeded,
        stripeSucceededPaymentIntent,
        stripePaymentIntentId,
        orderSaleItem,
        fetchingOrderSaleItem,
        listenForSaleItem,
        fetchSaleOrderItem,
    ]);

    /**
     * Listens for changes to sale item in Firestore
     */
    // keep all changing values fresh for the snapshot callback
    const latest = useLatest({
        stripePaymentIntentId,
        stripePaymentSucceeded,
        listenForSaleItem,
        lastSaleItemPingVersion,
        setLastSaleItemPingVersion,
        fetchSaleOrderItem,
        setListenForSaleItem,
        orderSaleItem,
        fetchingOrderSaleItem,
    });
    const unsubscribeRef = React.useRef<Unsubscribe | null>(null);
    // (Re)subscribe when the *conditions* change; do manual teardown inside.
    useEffect(() => {
        const cur = latest.current;

        // If we shouldn't be listening now, tear down and bail.
        if (!cur.stripePaymentIntentId || !cur.stripePaymentSucceeded || !cur.listenForSaleItem) {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
            return;
        }

        // If already subscribed, reset before creating a new one.
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }

        const db = getFirestore();
        const checkoutSessionsCollection = useStripeTestMode
            ? FIRESTORE_COLLECTION.stagingCheckoutSessions
            : FIRESTORE_COLLECTION.checkoutSessions;

        const pingRef = doc(db, `${checkoutSessionsCollection}/${cur.stripePaymentIntentId}/ping/current`);

        unsubscribeRef.current = onSnapshot(pingRef, async (pingSnapshot) => {
            const snapshotCur = latest.current;
            if (!pingSnapshot.exists()) {
                // no checkout session item update yet
                return;
            }

            // checkout session item but no ping item yet (will be made when checkout session item is next updated)
            const { version } = pingSnapshot.data() as { version: number };

            // ignore stale/no-op
            if (version <= snapshotCur.lastSaleItemPingVersion) return;

            snapshotCur.setLastSaleItemPingVersion(version);

            if (cur.orderSaleItem == null && !cur.fetchingOrderSaleItem) {
                const success = await snapshotCur.fetchSaleOrderItem();
                if (success) {
                    snapshotCur.setListenForSaleItem(false);
                    // stop listening after success
                    if (unsubscribeRef.current) {
                        unsubscribeRef.current();
                        unsubscribeRef.current = null;
                    }
                }
            }
        });
    }, [
        stripePaymentIntentId,
        stripePaymentSucceeded,
        listenForSaleItem,
    ]);
    // Unmount-only cleanup
    useEffect(() => () => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
    }, []);

    /**
     * Drives tax-resolution polling on-demand using phase/cycle state.
     * This keeps checks immediate and event-driven instead of time-based.
     */
    useEffect(() => {
        if (taxResolution.phase === TAX_RESOLUTION_PHASE.polling) {
            checkForTaxTransaction();
        }
    }, [
        taxResolution.phase,
        taxResolution.cycle,
    ]);

    useEffect(() => {
        if (orderSaleItem?.orderCost) {
            setOrderCost(orderSaleItem.orderCost);
            setTaxResolution((cur) => ({
                phase: TAX_RESOLUTION_PHASE.resolved,
                cycle: cur.cycle,
            }));
        }
    }, [orderSaleItem]);

    useEffect(() => {
        if (
            postPaymentCheckoutErrorNotified
            && !postPaymentCheckoutRecoveryNotified
            && orderSaleItem
        ) {
            setPostPaymentCheckoutRecoveryNotified(true);
            const message = 'We resolved a temporary order-processing issue. Your order details are now available.';
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: message,
                icon: PixelCartIcon,
            });
        }
    }, [
        postPaymentCheckoutErrorNotified,
        postPaymentCheckoutRecoveryNotified,
        orderSaleItem,
        currentSessionId,
    ]);

    // ===== Memoization =====

    const pageSubtitle = useMemo(() => {
        switch (params.checkpoint) {
        case CHECKOUT_CHECKPOINT.info:
            return 'Where should we deliver your haul?';
        case CHECKOUT_CHECKPOINT.shipping:
            return 'How should we deliver your haul?';
        case CHECKOUT_CHECKPOINT.billing:
            return 'Who should we expense?';
        case CHECKOUT_CHECKPOINT.review:
            return 'How\'s everything look?';
        default:
            // CHECKOUT_CHECKPOINT.confirmation
            return 'Mission accomplished!';
        }
    }, [params.checkpoint]);

    const checkpointPlaceholderText = useMemo(() => {
        switch (params.checkpoint) {
        case CHECKOUT_CHECKPOINT.info:
            if (!fetchedStoreItems) {
                return 'Fetching cart details';
            }
            if (
                attemptedRestoringCheckoutSessionAfterPayment
                && !attemptedResumingPreviousCheckoutSession
                && !currentCheckoutSessionId
                && !stripePaymentIntentId
                && !stripePaymentSucceeded
            ) {
                return 'Searching for checkout session to restore';
            }
            if (
                attemptedResumingPreviousCheckoutSession
                && attemptedRestoringCheckoutSessionAfterPayment
                && !currentCheckoutSessionId
            ) {
                return 'Creating new checkout session';
            }
            return 'Preparing checkout session';
        case CHECKOUT_CHECKPOINT.shipping:
            return 'Fetching shipping rates';
        case CHECKOUT_CHECKPOINT.billing:
            if (
                attemptedRestoringCheckoutSessionAfterPayment
                && !attemptedResumingPreviousCheckoutSession
                && !currentCheckoutSessionId
                && !stripePaymentIntentId
                && !stripePaymentSucceeded
            ) {
                return 'Searching for checkout session to restore';
            }
            if (
                attemptedResumingPreviousCheckoutSession
                && attemptedRestoringCheckoutSessionAfterPayment
                && !currentCheckoutSessionId
            ) {
                return 'Creating new checkout session';
            }
            return 'Loading Stripe payment service';
        case CHECKOUT_CHECKPOINT.review:
            return 'Aggregating checkout information';
        default:
            // CHECKOUT_CHECKPOINT.confirmation
            if (
                stripeSucceededPaymentIntent
                && orderSaleItem
            ) {
                return 'Summarizing order details';
            }
            if (stripeSucceededPaymentIntent) {
                return 'Fetching order details';
            }

            return 'Finalizing order purchase';
        }
    }, [
        params.checkpoint,
        fetchedStoreItems,
        stripeSucceededPaymentIntent,
        orderSaleItem,
        fetchedStoreItems,
        attemptedRestoringCheckoutSessionAfterPayment,
        attemptedResumingPreviousCheckoutSession,
        currentCheckoutSessionId,
        stripePaymentIntentId,
        stripePaymentSucceeded,
    ]);

    const PHYSICAL_BOOK_PRODUCT_ID = useMemo(() => (
        useStripeTestMode
            ? PHYSICAL_BOOK_STRIPE_PRODUCT_TEST_API_ID
            : PHYSICAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID
    ), [useStripeTestMode]);
    const PHYSICAL_BOOK_PREORDER_PRODUCT_ID = useMemo(() => (
        useStripeTestMode
            ? PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID
            : PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID
    ), [useStripeTestMode]);
    const DIGITAL_BOOK_PRODUCT_ID = useMemo(() => (
        useStripeTestMode
            ? DIGITAL_BOOK_STRIPE_PRODUCT_TEST_API_ID
            : DIGITAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID
    ), [useStripeTestMode]);
    const DIGITAL_BOOK_PREORDER_PRODUCT_ID = useMemo(() => (
        useStripeTestMode
            ? DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID
            : DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID
    ), [useStripeTestMode]);
    const WEB_BOOK_PRODUCT_ID = useMemo(() => (
        useStripeTestMode
            ? WEB_BOOK_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_STRIPE_PRODUCT_LIVE_API_ID
    ), [useStripeTestMode]);

    const complimentaryBookInviteQuantity = useMemo(() => {
        if (
            !userCartItems
            || userCartItems.length === 0
        ) return 0;

        return userCartItems.reduce((total: number, item: ICartItem) => (
            item.productId === PHYSICAL_BOOK_PRODUCT_ID
            || item.productId === DIGITAL_BOOK_PRODUCT_ID
            || item.productId === PHYSICAL_BOOK_PREORDER_PRODUCT_ID
            || item.productId === DIGITAL_BOOK_PREORDER_PRODUCT_ID
                ? total + item.quantity
                : total
        ), 0);
    }, [
        userCartItems,
        PHYSICAL_BOOK_PRODUCT_ID,
        DIGITAL_BOOK_PRODUCT_ID,
        PHYSICAL_BOOK_PREORDER_PRODUCT_ID,
        DIGITAL_BOOK_PREORDER_PRODUCT_ID,
    ]);
    const hadComplimentaryBookInviteAtCheckout = useMemo(() => (
        stripeSucceededPaymentIntent?.metadata?.hadComplimentaryBookInviteAtCheckout === 'true'
        || stripeSucceededPaymentIntent?.metadata?.hadComplimentaryWebBookAccessAtCheckout === 'true'
    ), [
        stripeSucceededPaymentIntent?.metadata?.hadComplimentaryBookInviteAtCheckout,
        stripeSucceededPaymentIntent?.metadata?.hadComplimentaryWebBookAccessAtCheckout,
    ]);
    const hasComplimentaryBookInviteForCheckout = useMemo(() => (
        complimentaryBookInviteQuantity > 0
        || hadComplimentaryBookInviteForCheckoutFlow
        || hadComplimentaryBookInviteAtCheckout
    ), [
        complimentaryBookInviteQuantity,
        hadComplimentaryBookInviteForCheckoutFlow,
        hadComplimentaryBookInviteAtCheckout,
    ]);
    const getComplimentaryBookInviteQuantity = useCallback((cartItems?: ICartItem[]): number => {
        if (
            !hasComplimentaryBookInviteForCheckout
            || !cartItems
            || cartItems.length === 0
        ) return 0;

        return cartItems.reduce((total: number, item: ICartItem) => (
            item.productId === PHYSICAL_BOOK_PRODUCT_ID
            || item.productId === DIGITAL_BOOK_PRODUCT_ID
            || item.productId === PHYSICAL_BOOK_PREORDER_PRODUCT_ID
            || item.productId === DIGITAL_BOOK_PREORDER_PRODUCT_ID
                ? total + item.quantity
                : total
        ), 0);
    }, [
        hasComplimentaryBookInviteForCheckout,
        PHYSICAL_BOOK_PRODUCT_ID,
        DIGITAL_BOOK_PRODUCT_ID,
        PHYSICAL_BOOK_PREORDER_PRODUCT_ID,
        DIGITAL_BOOK_PREORDER_PRODUCT_ID,
    ]);

    useEffect(() => {
        setHadComplimentaryBookInviteForCheckoutFlow(false);
    }, [stripePaymentIntentId]);

    const checkoutComplimentaryBookInviteQuantity = useMemo(() => (
        getComplimentaryBookInviteQuantity(userCartItems)
    ), [
        getComplimentaryBookInviteQuantity,
        userCartItems,
    ]);
    const checkoutPurchasedWebBookQuantity = useMemo(() => (
        userCartItems?.reduce((total: number, item: ICartItem) => (
            item.productId === WEB_BOOK_PRODUCT_ID
                ? total + item.quantity
                : total
        ), 0) || 0
    ), [
        userCartItems,
        WEB_BOOK_PRODUCT_ID,
    ]);
    const checkoutBookInvitesInOrderCount = useMemo(() => (
        checkoutComplimentaryBookInviteQuantity + checkoutPurchasedWebBookQuantity
    ), [
        checkoutComplimentaryBookInviteQuantity,
        checkoutPurchasedWebBookQuantity,
    ]);
    const succeededPaymentMetadataCartItems = useMemo(() => (
        stripeSucceededPaymentIntent
            ? getCartItemsFromPaymentIntent(stripeSucceededPaymentIntent)
            : undefined
    ), [
        stripeSucceededPaymentIntent,
    ]);
    const succeededComplimentaryBookInviteQuantity = useMemo(() => (
        getComplimentaryBookInviteQuantity(succeededPaymentMetadataCartItems)
    ), [
        getComplimentaryBookInviteQuantity,
        succeededPaymentMetadataCartItems,
    ]);

    const numCheckoutItems = useMemo(() => {
        let count = 0;
        if (
            user
            && userCartItems
            && userCartItems.length > 0
        ) {
            userCartItems.forEach((item: ICartItem) => {
                count += item.quantity;
            });
            // Add complimentary Book Invite quantity matching qualifying items.
            count += checkoutComplimentaryBookInviteQuantity;
        } else if (
            user
            && stripeSucceededPaymentIntent
            && stripeSucceededPaymentIntent
            && stripeSucceededPaymentIntent.metadata.cartItems
        ) {
            const metadataCartItems = succeededPaymentMetadataCartItems || [];
            metadataCartItems.forEach((item: ICartItem) => {
                count += item.quantity;
            });
            // Add complimentary Book Invite quantity matching qualifying items.
            count += succeededComplimentaryBookInviteQuantity;
        }
        return count;
    }, [
        userCartItems,
        stripePaymentSucceeded,
        stripeSucceededPaymentIntent,
        orderSaleItem,
        hasComplimentaryBookInviteForCheckout,
        checkoutComplimentaryBookInviteQuantity,
        succeededPaymentMetadataCartItems,
        succeededComplimentaryBookInviteQuantity,
        stripePaymentSucceeded,
    ]);

    const breadcrumbItems = useMemo(() => {
        if (physicalItemsInCart.length > 0) {
            return [
                {
                    text: 'Cart',
                    route: PAGE_ROUTE.cart,
                },
                {
                    text: 'Info',
                    route: `${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.info}`,
                    interactable: addressIsValid,
                },
                {
                    text: 'Shipping',
                    abbrText: 'Ship',
                    route: `${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.shipping}`,
                    interactable: !!selectedShippingRateID,
                },
                {
                    text: 'Billing',
                    abbrText: 'Bill',
                    route: `${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.billing}`,
                    interactable: !!selectedBillingAddressType
                        && stripePaymentDetailsCompleted,
                },
                {
                    text: 'Review',
                    route: `${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.review}`,
                    interactable: addressIsValid
                        && !!selectedShippingRateID
                        && !!selectedBillingAddressType
                        && billingAddressIsValid
                        && stripePaymentDetailsCompleted
                        && customerDetailsCompleted,
                },
            ];
        }

        return [
            {
                text: 'Cart',
                route: PAGE_ROUTE.cart,
            },
            {
                text: 'Billing',
                abbrText: 'Bill',
                route: `${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.billing}`,
                interactable: !!selectedBillingAddressType
                    && stripePaymentDetailsCompleted,
            },
            {
                text: 'Review',
                route: `${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.review}`,
                interactable: (addressIsValid || physicalItemsInCart.length === 0)
                    && (!!selectedShippingRateID || physicalItemsInCart.length === 0)
                    && !!selectedBillingAddressType
                    && billingAddressIsValid
                    && stripePaymentDetailsCompleted
                    && customerDetailsCompleted,
            },
        ];
    }, [
        physicalItemsInCart,
        addressIsValid,
        selectedShippingRateID,
        selectedBillingAddressType,
        stripePaymentSucceeded,
        stripePaymentDetailsCompleted,
        billingAddressIsValid,
        customerDetailsCompleted,
    ]);

    const succeededPaymentCartItems = useMemo(() => succeededPaymentMetadataCartItems, [
        succeededPaymentMetadataCartItems,
    ]);

    const promotionAddButtonInteractionProperties = useMemo(() => {
        const properties: Record<string, string | (
            (e: React.TouchEvent) => void) | ((e: React.MouseEvent) => void
        )> = !promotionCodeInvalid && !isValidatingPromotionCode
            ? {
                className: HOVER_TARGET_CLASSNAME,
            } : {};

        if (
            !promotionCodeInvalid
            && !isValidatingPromotionCode
            && detectTouchDevice(document)
        ) {
            properties.onTouchStart = (e: React.TouchEvent) => onPromotionCodeAddButtonEnter(e);
            properties.onTouchEnd = (e: React.TouchEvent) => onPromotionCodeAddButtonLeave(e);
        } else if (
            !promotionCodeInvalid
            && !isValidatingPromotionCode
            && !detectTouchDevice(document)
        ) {
            properties.onMouseEnter = (e: React.TouchEvent) => onPromotionCodeAddButtonEnter(e);
            properties.onMouseLeave = (e: React.TouchEvent) => onPromotionCodeAddButtonLeave(e);
        }

        return properties;
    }, [
        isValidatingPromotionCode,
        promotionCodeInvalid,
        document,
    ]);

    const appearance: Appearance = {
        theme: 'stripe',
        variables: {
            colorPrimary: theme.verascopeColor.purple400,
            colorBackground: theme.color.white,
            colorText: theme.verascopeColor.purple100,
            colorDanger: theme.verascopeColor.red200,
        },
    };
    const stripeOptions: StripeElementsOptions = {
        clientSecret: stripeClientSecret,
        appearance,
    };

    const displayCartItems = (userCartItems || []).map((item) => {
        const product = negotiationPreview?.price.product as Stripe.Product | undefined;
        if (!product || item.productId !== product.id) return item;
        return { ...item, saleNegotiationPriceId: negotiationPreview!.price.id };
    });
    const displayStoreItems = new Map(storeItemByPrice);
    if (negotiationPreview) {
        const product = negotiationPreview.price.product as Stripe.Product;
        const original = Array.from(storeItemByPrice.values()).find((item) => item.productId === product.id);
        if (original) {
            displayStoreItems.set(negotiationPreview.price.id, {
                ...original, priceId: negotiationPreview.price.id, amount: negotiationPreview.price.unit_amount,
            });
        }
    }
    let displayedOrderCost = orderCost;
    if (negotiationPreview && orderCost) {
        const subtotal = calculateDiscountedCartSubtotal({
            cartItems: displayCartItems, promotions: appliedPromotions, storeItemsByPrice: displayStoreItems,
        });
        displayedOrderCost = {
            ...orderCost,
            subtotal,
            total: (orderCost.total ?? orderCost.subtotal) + subtotal - orderCost.subtotal,
        };
    }

    return (
        <Container
            ref={containerRef}
        >
            <PageLogo
                dark
                withEnterTransition
                className={HOVER_TARGET_CLASSNAME}
                customCursorActive={customCursorActive}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: (e) => onLogoEnter(e),
                    onMouseLeave: (e) => onLogoLeave(e),
                })}
                {...getPressEventHandlers({ onMouseDown: onLogoClick })}
            >
                <ReactSVG
                    src={VerascopeLogo}
                />
            </PageLogo>
            <PageTitle
                withEnterTransition
                color={theme.verascopeColor.purple200}
            >
                Checkout
            </PageTitle>
            <PageSubtitle
                withEnterTransition
                color={theme.verascopeColor.purple400}
                hideOnExtraSmall
            >
                {pageSubtitle}
            </PageSubtitle>
            <ContentContainer>
                <OrderSummaryContainer>
                    {viewportDimensions.width >= MEDIA_QUERY_SIZE.small.min
                        ? (
                            <Header>
                                <HeaderText>
                                    {(
                                        (
                                            displayCartItems
                                            && !stripePaymentSucceeded
                                        ) || (
                                            stripePaymentSucceeded
                                            && stripeSucceededPaymentIntent
                                        )
                                    ) ? (
                                            <>
                                                {`Item${numCheckoutItems !== 1 ? 's' : ''}`}
                                                {(
                                                    succeededPaymentCartItems
                                                    || (
                                                        fetchedStoreItems
                                                        && displayCartItems
                                                    )
                                                ) && (
                                                    <CartItemsCountText>
                                                        {numCheckoutItems}
                                                    </CartItemsCountText>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                Items
                                            </>
                                        )}
                                </HeaderText>
                            </Header>
                        ) : (
                            <Header>
                                <HeaderText>
                                    Order Summary
                                </HeaderText>
                                <OrderSummaryToggleButtonContainer
                                    className={HOVER_TARGET_CLASSNAME}
                                    orderSummaryIsVisible={orderSummaryIsVisible}
                                    detectTouchDevice={detectTouchDevice(document)}
                                    {...getTouchMouseEventHandlers({
                                        onMouseEnter: (e) => onOrderSummaryToggleButtonEnter(e),
                                        onMouseLeave: (e) => onOrderSummaryToggleButtonLeave(e),
                                    })}
                                    {...getPressEventHandlers({ onMouseDown: onOrderSummaryToggleButtonClick })}
                                >
                                    <OrderSummaryToggleButton />
                                </OrderSummaryToggleButtonContainer>
                            </Header>
                        )}
                    <OrderSummary
                        isVisible={viewportDimensions.width >= MEDIA_QUERY_SIZE.small.min || orderSummaryIsVisible}
                    >
                        <CartItemsContainer>
                            {stripePaymentSucceeded
                            && stripeSucceededPaymentIntent
                            && succeededPaymentCartItems
                            && (
                                <CartItems>
                                    {succeededPaymentCartItems.map((cartItem) => {
                                        const storeItem = displayStoreItems.get(
                                            cartItem.saleNegotiationPriceId || cartItem.priceId,
                                        );
                                        if (!storeItem) return null;
                                        let storeItemAmount = storeItem.amount!;
                                        let saleNegotationStoreItem: IStoreItem | undefined;
                                        if (
                                            cartItem.saleNegotiationPriceId
                                            && displayStoreItems.get(cartItem.saleNegotiationPriceId)
                                        ) {
                                            saleNegotationStoreItem = displayStoreItems.get(cartItem.saleNegotiationPriceId)!;
                                            storeItemAmount = saleNegotationStoreItem.amount!;
                                        }
                                        const hasOncePerOrderPromotion = appliedPromotions.some(
                                            promotionAppliesOncePerOrder,
                                        );
                                        let cumulativeDiscount = 0;
                                        if (appliedPromotions.length > 0) {
                                            for (let i = 0; i < appliedPromotions.length; i += 1) {
                                                const promotion = appliedPromotions[i];
                                                const discountedAmount = applyPromotionToCheckoutItem(
                                                    saleNegotationStoreItem || storeItem,
                                                    promotion,
                                                );
                                                cumulativeDiscount += storeItemAmount - discountedAmount;
                                            }
                                        }
                                        const discounted = appliedPromotions.length > 0
                                        && cumulativeDiscount > 0;

                                        return (
                                            <CartItemContainer
                                                key={cartItem.priceId}
                                                small
                                            >
                                                <CartItemImageContainer
                                                    small
                                                    hasImage={storeItem.imageURLs.length > 0}
                                                >
                                                    {storeItem.imageURLs.length > 0
                                                        ? (
                                                            <CartItemImage
                                                                url={storeItem.imageURLs[0]}
                                                            />
                                                        ) : (
                                                            <ReactSVG
                                                                src={BoxIcon}
                                                            />
                                                        )}
                                                </CartItemImageContainer>
                                                <CartItemDetails>
                                                    <CartItemName
                                                        small
                                                    >
                                                        {storeItem.name}
                                                    </CartItemName>
                                                    <CartItemPrice
                                                        small
                                                        spaceBetween
                                                    >
                                                        {discounted || cartItem.saleNegotiationPriceId
                                                            ? (
                                                                <DiscountedCartItemPrice>
                                                                    {`$${roundToNDecimals(storeItem.amount! / CENTS_IN_A_DOLLAR, 2)}`}
                                                                </DiscountedCartItemPrice>
                                                            ) : `$${roundToNDecimals(storeItem.amount! / CENTS_IN_A_DOLLAR, 2)}`}
                                                        {(discounted || cartItem.saleNegotiationPriceId) && (
                                                            <CartItemPriceAfterDiscount>
                                                                {` $${roundToNDecimals((storeItemAmount - cumulativeDiscount) / CENTS_IN_A_DOLLAR, 2)}${
                                                                    hasOncePerOrderPromotion
                                                                    && cartItem.quantity > 1
                                                                        ? ' (1)'
                                                                        : ''
                                                                }`}
                                                            </CartItemPriceAfterDiscount>
                                                        )}
                                                        <CartItemFinalQuantityContainer>
                                                            Qty
                                                            <CartItemFinalQuantityValue>
                                                                {cartItem.quantity}
                                                            </CartItemFinalQuantityValue>
                                                        </CartItemFinalQuantityContainer>
                                                    </CartItemPrice>
                                                </CartItemDetails>
                                            </CartItemContainer>
                                        );
                                    })}
                                    {succeededComplimentaryBookInviteQuantity > 0
                                    && webBookStoreItem
                                    && (
                                        <CartItemContainer
                                            small
                                        >
                                            <CartItemImageContainer
                                                small
                                                hasImage={webBookStoreItem.imageURLs.length > 0}
                                            >
                                                {webBookStoreItem.imageURLs.length > 0
                                                    ? (
                                                        <CartItemImage
                                                            url={webBookStoreItem.imageURLs[0]}
                                                        />
                                                    ) : (
                                                        <ReactSVG
                                                            src={BoxIcon}
                                                        />
                                                    )}
                                            </CartItemImageContainer>
                                            <CartItemDetails>
                                                <CartItemName
                                                    small
                                                >
                                                    {`${webBookStoreItem.name} (Complimentary)`}
                                                </CartItemName>
                                                <CartItemPrice
                                                    small
                                                    spaceBetween
                                                >
                                                    <DiscountedCartItemPrice>
                                                        {`$${roundToNDecimals(
                                                            (webBookStoreItem.amount! * succeededComplimentaryBookInviteQuantity) / CENTS_IN_A_DOLLAR,
                                                            2,
                                                        )}`}
                                                    </DiscountedCartItemPrice>
                                                    <CartItemPriceAfterDiscount>
                                                        {' $0.00'}
                                                    </CartItemPriceAfterDiscount>
                                                    <CartItemFinalQuantityContainer>
                                                        Qty
                                                        <CartItemFinalQuantityValue>
                                                            {succeededComplimentaryBookInviteQuantity}
                                                        </CartItemFinalQuantityValue>
                                                    </CartItemFinalQuantityContainer>
                                                </CartItemPrice>
                                            </CartItemDetails>
                                        </CartItemContainer>
                                    )}
                                </CartItems>
                            )}
                            {!stripePaymentSucceeded
                            && !stripeSucceededPaymentIntent
                            && (
                                <CartItems>
                                    {fetchedStoreItems
                                    && user
                                    && displayCartItems
                                    && (
                                        displayCartItems.map((cartItem) => {
                                            const storeItem = displayStoreItems.get(
                                                cartItem.saleNegotiationPriceId || cartItem.priceId,
                                            );
                                            if (!storeItem) return null;
                                            let storeItemAmount = storeItem.amount!;
                                            let saleNegotationStoreItem: IStoreItem | undefined;
                                            if (
                                                cartItem.saleNegotiationPriceId
                                                && displayStoreItems.get(cartItem.saleNegotiationPriceId)
                                            ) {
                                                saleNegotationStoreItem = displayStoreItems.get(cartItem.saleNegotiationPriceId)!;
                                                storeItemAmount = saleNegotationStoreItem.amount!;
                                            }
                                            const hasOncePerOrderPromotion = appliedPromotions.some(
                                                promotionAppliesOncePerOrder,
                                            );
                                            let cumulativeDiscount = 0;
                                            if (appliedPromotions.length > 0) {
                                                for (let i = 0; i < appliedPromotions.length; i += 1) {
                                                    const promotion = appliedPromotions[i];
                                                    const discountedAmount = applyPromotionToCheckoutItem(
                                                        saleNegotationStoreItem || storeItem,
                                                        promotion,
                                                    );
                                                    cumulativeDiscount += storeItemAmount - discountedAmount;
                                                }
                                            }
                                            const discounted = appliedPromotions.length > 0
                                            && cumulativeDiscount > 0;

                                            return (
                                                <CartItemContainer
                                                    key={cartItem.saleNegotiationPriceId || cartItem.priceId}
                                                    small
                                                >
                                                    <CartItemImageContainer
                                                        small
                                                        hasImage={storeItem.imageURLs.length > 0}
                                                    >
                                                        {storeItem.imageURLs.length > 0
                                                            ? (
                                                                <CartItemImage
                                                                    url={storeItem.imageURLs[0]}
                                                                />
                                                            ) : (
                                                                <ReactSVG
                                                                    src={BoxIcon}
                                                                />
                                                            )}
                                                    </CartItemImageContainer>
                                                    <CartItemDetails>
                                                        <CartItemName
                                                            small
                                                        >
                                                            {storeItem.name}
                                                        </CartItemName>
                                                        <CartItemPrice
                                                            small
                                                            spaceBetween
                                                        >
                                                            {discounted || cartItem.saleNegotiationPriceId
                                                                ? (
                                                                    <DiscountedCartItemPrice>
                                                                        {`$${roundToNDecimals(storeItem.amount! / CENTS_IN_A_DOLLAR, 2)}`}
                                                                    </DiscountedCartItemPrice>
                                                                ) : `$${roundToNDecimals(storeItem.amount! / CENTS_IN_A_DOLLAR, 2)}`}
                                                            {(discounted || cartItem.saleNegotiationPriceId) && (
                                                                <CartItemPriceAfterDiscount>
                                                                    {` $${roundToNDecimals((storeItemAmount - cumulativeDiscount) / CENTS_IN_A_DOLLAR, 2)}${
                                                                        hasOncePerOrderPromotion
                                                                        && cartItem.quantity > 1
                                                                            ? ' (1)'
                                                                            : ''
                                                                    }`}
                                                                </CartItemPriceAfterDiscount>
                                                            )}
                                                            <CartItemFinalQuantityContainer>
                                                                Qty
                                                                <CartItemFinalQuantityValue>
                                                                    {cartItem.quantity}
                                                                </CartItemFinalQuantityValue>
                                                            </CartItemFinalQuantityContainer>
                                                        </CartItemPrice>
                                                    </CartItemDetails>
                                                </CartItemContainer>
                                            );
                                        })
                                    )}
                                    {checkoutComplimentaryBookInviteQuantity > 0
                                    && webBookStoreItem
                                    && (
                                        <CartItemContainer
                                            small
                                        >
                                            <CartItemImageContainer
                                                small
                                                hasImage={webBookStoreItem.imageURLs.length > 0}
                                            >
                                                {webBookStoreItem.imageURLs.length > 0
                                                    ? (
                                                        <CartItemImage
                                                            url={webBookStoreItem.imageURLs[0]}
                                                        />
                                                    ) : (
                                                        <ReactSVG
                                                            src={BoxIcon}
                                                        />
                                                    )}
                                            </CartItemImageContainer>
                                            <CartItemDetails>
                                                <CartItemName
                                                    small
                                                >
                                                    {`${webBookStoreItem.name} (Complimentary)`}
                                                </CartItemName>
                                                <CartItemPrice
                                                    small
                                                    spaceBetween
                                                >
                                                    <DiscountedCartItemPrice>
                                                        {`$${roundToNDecimals(
                                                            (webBookStoreItem.amount! * checkoutComplimentaryBookInviteQuantity) / CENTS_IN_A_DOLLAR,
                                                            2,
                                                        )}`}
                                                    </DiscountedCartItemPrice>
                                                    <CartItemPriceAfterDiscount>
                                                        {' $0.00'}
                                                    </CartItemPriceAfterDiscount>
                                                    <CartItemFinalQuantityContainer>
                                                        Qty
                                                        <CartItemFinalQuantityValue>
                                                            {checkoutComplimentaryBookInviteQuantity}
                                                        </CartItemFinalQuantityValue>
                                                    </CartItemFinalQuantityContainer>
                                                </CartItemPrice>
                                            </CartItemDetails>
                                        </CartItemContainer>
                                    )}
                                </CartItems>
                            )}
                            {(
                                (
                                    !stripePaymentSucceeded
                                    && !stripeSucceededPaymentIntent
                                    && (
                                        !fetchedStoreItems
                                        || !displayCartItems
                                        || displayCartItems.length === 0
                                    )
                                ) || (
                                    stripePaymentSucceeded
                                    && !succeededPaymentCartItems
                                )
                            )
                            && (
                                <>
                                    <CartItemContainer
                                        small
                                    >
                                        <CartItemImageContainer
                                            small
                                            hasImage={false}
                                        >
                                            <ReactSVG
                                                src={BoxIcon}
                                            />
                                        </CartItemImageContainer>
                                        <CartItemDetails>
                                            <CartItemName
                                                small
                                            >
                                                <PlaceholderBox
                                                    width={CART_ITEM_NAME_PLACEHOLDER_WIDTH}
                                                    height={CART_ITEM_NAME_PLACEHOLDER_HEIGHT}
                                                />
                                            </CartItemName>
                                            <CartItemPrice
                                                small
                                                spaceBetween
                                            >
                                                <PlaceholderBox
                                                    width={CART_ITEM_PRICE_PLACEHOLDER_WIDTH}
                                                    height={CART_ITEM_PRICE_PLACEHOLDER_HEIGHT}
                                                />
                                            </CartItemPrice>
                                        </CartItemDetails>
                                    </CartItemContainer>
                                    <CartItemContainer
                                        small
                                    >
                                        <CartItemImageContainer
                                            small
                                            hasImage={false}
                                        >
                                            <ReactSVG
                                                src={BoxIcon}
                                            />
                                        </CartItemImageContainer>
                                        <CartItemDetails>
                                            <CartItemName
                                                small
                                            >
                                                <PlaceholderBox
                                                    width={CART_ITEM_NAME_PLACEHOLDER_WIDTH}
                                                    height={CART_ITEM_NAME_PLACEHOLDER_HEIGHT}
                                                />
                                            </CartItemName>
                                            <CartItemPrice
                                                small
                                                spaceBetween
                                            >
                                                <PlaceholderBox
                                                    width={CART_ITEM_PRICE_PLACEHOLDER_WIDTH}
                                                    height={CART_ITEM_PRICE_PLACEHOLDER_HEIGHT}
                                                />
                                            </CartItemPrice>
                                        </CartItemDetails>
                                    </CartItemContainer>
                                    <CartItemContainer
                                        small
                                    >
                                        <CartItemImageContainer
                                            small
                                            hasImage={false}
                                        >
                                            <ReactSVG
                                                src={BoxIcon}
                                            />
                                        </CartItemImageContainer>
                                        <CartItemDetails>
                                            <CartItemName
                                                small
                                            >
                                                <PlaceholderBox
                                                    width={CART_ITEM_NAME_PLACEHOLDER_WIDTH}
                                                    height={CART_ITEM_NAME_PLACEHOLDER_HEIGHT}
                                                />
                                            </CartItemName>
                                            <CartItemPrice
                                                small
                                                spaceBetween
                                            >
                                                <PlaceholderBox
                                                    width={CART_ITEM_PRICE_PLACEHOLDER_WIDTH}
                                                    height={CART_ITEM_PRICE_PLACEHOLDER_HEIGHT}
                                                />
                                            </CartItemPrice>
                                        </CartItemDetails>
                                    </CartItemContainer>
                                </>
                            )}
                        </CartItemsContainer>
                        {currentCheckoutSessionId && (
                            <PromotionCodeContainer
                                noBorder={params.checkpoint === CHECKOUT_CHECKPOINT.confirmation}
                            >
                                {(
                                    appliedPromotions.length > 0
                                    || (
                                        user
                                        && !stripePaymentSucceeded
                                        && !stripeSucceededPaymentIntent
                                        && displayCartItems
                                        && displayCartItems.filter((item: ICartItem) => item.saleNegotiationPriceId).length > 0
                                    ) || (
                                        stripePaymentSucceeded
                                        && stripeSucceededPaymentIntent
                                        && succeededPaymentCartItems
                                        && succeededPaymentCartItems.filter((item: ICartItem) => item.saleNegotiationPriceId).length > 0
                                    )
                                ) && (
                                    <PromotionCodeBadgeContainer>
                                        {appliedPromotions.map((promotion) => (
                                            <PromotionCodeBadge
                                                key={promotion.id}
                                                data-testid={`checkout-promotion-badge-${promotion.id}`}
                                            >
                                                {promotion.name}
                                                {params.checkpoint !== CHECKOUT_CHECKPOINT.confirmation && (
                                                    <PromotionCodeBadgeRemoveButton
                                                        data-testid={`checkout-promotion-remove-${promotion.id}`}
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        detectTouchDevice={detectTouchDevice(document)}
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: (e) => onPromotionCodeBadgeRemoveButtonEnter(e),
                                                            onMouseLeave: (e) => onPromotionCodeBadgeRemoveButtonLeave(e),
                                                        })}
                                                        {...getPressEventHandlers({
                                                            onMouseDown: (e) => onPromotionCodeBadgeRemoveButtonClick(
                                                                promotion.id,
                                                                STRIPE_PROMOTION_VALID_ITEM_TYPE.coupon,
                                                                e,
                                                            ),
                                                        })}
                                                    >
                                                        <ReactSVG
                                                            src={PixelCrossIcon}
                                                        />
                                                    </PromotionCodeBadgeRemoveButton>
                                                )}
                                            </PromotionCodeBadge>
                                        ))}
                                        {user
                                        && !stripePaymentSucceeded
                                        && !stripeSucceededPaymentIntent
                                        && displayCartItems
                                        && displayCartItems
                                            .filter((item: ICartItem) => item.saleNegotiationPriceId)
                                            .map((item: ICartItem) => (
                                                <PromotionCodeBadge
                                                    key={item.saleNegotiationPriceId!}
                                                    data-testid={`checkout-promotion-price-badge-${item.saleNegotiationPriceId!}`}
                                                >
                                                    {item.saleNegotiationPriceId!}
                                                    {params.checkpoint !== CHECKOUT_CHECKPOINT.confirmation && (
                                                        <PromotionCodeBadgeRemoveButton
                                                            data-testid={`checkout-promotion-remove-price-${item.saleNegotiationPriceId!}`}
                                                            className={HOVER_TARGET_CLASSNAME}
                                                            customCursorActive={customCursorActive}
                                                            detectTouchDevice={detectTouchDevice(document)}
                                                            {...getTouchMouseEventHandlers({
                                                                onMouseEnter: (e) => onPromotionCodeBadgeRemoveButtonEnter(e),
                                                                onMouseLeave: (e) => onPromotionCodeBadgeRemoveButtonLeave(e),
                                                            })}
                                                            {...getPressEventHandlers({
                                                                onMouseDown: (e) => onPromotionCodeBadgeRemoveButtonClick(
                                                                    item.saleNegotiationPriceId!,
                                                                    STRIPE_PROMOTION_VALID_ITEM_TYPE.price,
                                                                    e,
                                                                ),
                                                            })}
                                                        >
                                                            <ReactSVG
                                                                src={PixelCrossIcon}
                                                            />
                                                        </PromotionCodeBadgeRemoveButton>
                                                    )}
                                                </PromotionCodeBadge>
                                            ))}
                                        {stripePaymentSucceeded
                                        && stripeSucceededPaymentIntent
                                        && succeededPaymentCartItems
                                        && succeededPaymentCartItems
                                            .filter((item: ICartItem) => item.saleNegotiationPriceId)
                                            .map((item: ICartItem) => (
                                                <PromotionCodeBadge
                                                    key={item.saleNegotiationPriceId!}
                                                    data-testid={`checkout-promotion-price-badge-${item.saleNegotiationPriceId!}`}
                                                >
                                                    {item.saleNegotiationPriceId!}
                                                    {params.checkpoint !== CHECKOUT_CHECKPOINT.confirmation && (
                                                        <PromotionCodeBadgeRemoveButton
                                                            data-testid={`checkout-promotion-remove-price-${item.saleNegotiationPriceId!}`}
                                                            className={HOVER_TARGET_CLASSNAME}
                                                            customCursorActive={customCursorActive}
                                                            detectTouchDevice={detectTouchDevice(document)}
                                                            {...getTouchMouseEventHandlers({
                                                                onMouseEnter: (e) => onPromotionCodeBadgeRemoveButtonEnter(e),
                                                                onMouseLeave: (e) => onPromotionCodeBadgeRemoveButtonLeave(e),
                                                            })}
                                                            {...getPressEventHandlers({
                                                                onMouseDown: (e) => onPromotionCodeBadgeRemoveButtonClick(
                                                                    item.saleNegotiationPriceId!,
                                                                    STRIPE_PROMOTION_VALID_ITEM_TYPE.price,
                                                                    e,
                                                                ),
                                                            })}
                                                        >
                                                            <ReactSVG
                                                                src={PixelCrossIcon}
                                                            />
                                                        </PromotionCodeBadgeRemoveButton>
                                                    )}
                                                </PromotionCodeBadge>
                                            ))}
                                    </PromotionCodeBadgeContainer>
                                )}
                                {params.checkpoint !== CHECKOUT_CHECKPOINT.confirmation
                                && (
                                    <PromotionCodeHeader
                                        className={HOVER_TARGET_CLASSNAME}
                                        customCursorActive={customCursorActive}
                                        {...getTouchMouseEventHandlers({
                                            onMouseEnter: (e) => onPromotionCodeToggleButtonEnter(e),
                                            onMouseLeave: (e) => onPromotionCodeToggleButtonLeave(e),
                                        })}
                                        {...getPressEventHandlers({ onMouseDown: onPromotionCodeToggleButtonClick })}
                                    >
                                        <PromotionCodeHeaderText>
                                            Promotion Code
                                        </PromotionCodeHeaderText>
                                        <PromotionCodeToggleButtonContainer
                                            data-testid="checkout-promotion-toggle"
                                            customCursorActive={customCursorActive}
                                            promotionCodeInputIsVisible={promotionCodeInputIsVisible}
                                            detectTouchDevice={detectTouchDevice(document)}
                                        >
                                            <PromotionCodeToggleButton />
                                        </PromotionCodeToggleButtonContainer>
                                    </PromotionCodeHeader>
                                )}
                                <PromotionCodeBody
                                    isVisible={promotionCodeInputIsVisible}
                                >
                                    <PromotionCodeInput
                                        data-testid="checkout-promotion-input"
                                        type="text"
                                        ref={promotionCodeInputRef}
                                        className={HOVER_TARGET_CLASSNAME}
                                        customCursorActive={customCursorActive}
                                        placeholder="CODE"
                                        onChange={onPromotionCodeInputChange}
                                        onKeyUp={checkForEnter}
                                        isInvalid={promotionCodeInvalid}
                                        addButtonVisible={promotionCode.length > 0 || isValidatingPromotionCode}
                                        disabled={false}
                                        {...getTouchMouseEventHandlers({
                                            onMouseEnter: (e) => onPromotionCodeInputEnter(e),
                                            onMouseLeave: (e) => onPromotionCodeInputLeave(e),
                                        })}
                                        onFocus={onPromotionCodeInputFocus}
                                        onBlur={onPromotionCodeInputBlur}
                                    />
                                    {(promotionCode.length > 0 || isValidatingPromotionCode) && (
                                        <PromotionCodeAddButton
                                            data-testid="checkout-promotion-add"
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            isInvalid={promotionCodeInvalid}
                                            isValidatingCode={isValidatingPromotionCode}
                                            detectTouchDevice={detectTouchDevice(document)}
                                            {...(promotionAddButtonInteractionProperties)}
                                            {...(!promotionCodeInvalid && !isValidatingPromotionCode
                                                ? {
                                                    onMouseDown: onPromotionCodeAddButtonClick,
                                                } : {})}
                                        >
                                            {!isValidatingPromotionCode
                                                ? 'Add'
                                                : (
                                                    <SpinnerContainer
                                                        src={Spinner}
                                                        alt="spinner"
                                                    />
                                                )}
                                        </PromotionCodeAddButton>
                                    )}
                                </PromotionCodeBody>
                            </PromotionCodeContainer>
                        )}
                        <OrderSummaryEntries>
                            <OrderSummaryEntry>
                                <OrderSummaryText>
                                    Subtotal
                                </OrderSummaryText>
                                <OrderSummaryPrice>
                                    {displayedOrderCost
                                        ? `$${roundToNDecimals(displayedOrderCost.subtotal / CENTS_IN_A_DOLLAR, 2)}`
                                        : (
                                            <PlaceholderBox
                                                width={ORDER_SUMMARY_PRICE_PLACEHOLDER_WIDTH}
                                                height={ORDER_SUMMARY_PRICE_PLACEHOLDER_HEIGHT}
                                            />
                                        )}
                                </OrderSummaryPrice>
                            </OrderSummaryEntry>
                            {physicalItemsInCart.length > 0 && (
                                <OrderSummaryEntry>
                                    <OrderSummaryText>
                                        Shipping
                                    </OrderSummaryText>
                                    <OrderSummaryPrice>
                                        {!!displayedOrderCost && (displayedOrderCost.shipping ? `$${roundToNDecimals(displayedOrderCost.shipping / CENTS_IN_A_DOLLAR, 2)}` : 'TBD')}
                                        {!displayedOrderCost && (
                                            <PlaceholderBox
                                                width={ORDER_SUMMARY_PRICE_PLACEHOLDER_WIDTH}
                                                height={ORDER_SUMMARY_PRICE_PLACEHOLDER_HEIGHT}
                                            />
                                        )}
                                    </OrderSummaryPrice>
                                </OrderSummaryEntry>
                            )}
                            <OrderSummaryEntry>
                                <OrderSummaryText>
                                    Tax
                                </OrderSummaryText>
                                <OrderSummaryPrice>
                                    {!!displayedOrderCost && (displayedOrderCost.tax != null ? `$${roundToNDecimals(displayedOrderCost.tax / CENTS_IN_A_DOLLAR, 2)}` : 'TBD')}
                                    {!displayedOrderCost && (
                                        <PlaceholderBox
                                            width={ORDER_SUMMARY_PRICE_PLACEHOLDER_WIDTH}
                                            height={ORDER_SUMMARY_PRICE_PLACEHOLDER_HEIGHT}
                                        />
                                    )}
                                </OrderSummaryPrice>
                            </OrderSummaryEntry>
                            <OrderSummaryEntry
                                divider
                            >
                                <OrderSummaryText>
                                    Total
                                </OrderSummaryText>
                                <OrderSummaryPrice>
                                    {!!displayedOrderCost && (displayedOrderCost.total != null ? `$${roundToNDecimals(displayedOrderCost.total / CENTS_IN_A_DOLLAR, 2)}` : 'TBD')}
                                    {!displayedOrderCost && (
                                        <PlaceholderBox
                                            width={ORDER_SUMMARY_PRICE_PLACEHOLDER_WIDTH}
                                            height={ORDER_SUMMARY_PRICE_PLACEHOLDER_HEIGHT}
                                        />
                                    )}
                                </OrderSummaryPrice>
                            </OrderSummaryEntry>
                        </OrderSummaryEntries>
                        <OrderSummaryDisclaimer>
                            No hidden fees. Taxes and shipping are shown before payment.
                        </OrderSummaryDisclaimer>
                    </OrderSummary>
                </OrderSummaryContainer>
                <CheckpointContainer>
                    {(
                        params.checkpoint !== CHECKOUT_CHECKPOINT.confirmation
                        && currentCheckoutSessionId
                    ) || (
                        params.checkpoint === CHECKOUT_CHECKPOINT.confirmation
                        && !!stripeSucceededPaymentIntent
                    )
                        ? (
                            <>
                                {!stripePaymentSucceeded
                                && !stripeSucceededPaymentIntent
                                && (
                                    <BreadcrumbContainer>
                                        <Breadcrumb
                                            lightBackground
                                            customCursorActive={customCursorActive}
                                            fontFamily={FONT_TYPE.PRESS_START}
                                            items={breadcrumbItems}
                                            chevronSrc={PixelChevronIcon}
                                            activeColor={theme.verascopeColor.orange200}
                                            interactableColor={theme.verascopeColor.purple400}
                                            hasSound={hasSound}
                                            currentSessionId={currentSessionId}
                                            viewportDimensions={viewportDimensions}
                                            onCursorEnter={onCursorEnter}
                                            onCursorLeave={onCursorLeave}
                                        />
                                    </BreadcrumbContainer>
                                )}
                                <Elements
                                    options={stripeOptions}
                                    stripe={stripePromise}
                                >
                                    <CheckoutCheckpoint
                                        customCursorActive={customCursorActive}
                                        hasSound={hasSound}
                                        user={user}
                                        userEmail={userEmail}
                                        userCartItems={userCartItems}
                                        bookInvitesInOrderCount={checkoutBookInvitesInOrderCount}
                                        currentSessionId={currentSessionId}
                                        checkpoint={params.checkpoint}
                                        fetchedStoreItems={fetchedStoreItems}
                                        physicalItemsInCart={physicalItemsInCart}
                                        stripePaymentIntentId={stripePaymentIntentId}
                                        stripeClientSecret={stripeClientSecret}
                                        stripeCustomerId={stripeCustomerId}
                                        stripeTaxCalculationId={stripeTaxCalculationId}
                                        savedAddresses={savedAddresses}
                                        savedStripePaymentMethods={savedStripePaymentMethods}
                                        customerStreetOne={customerStreetOne}
                                        addressIsValid={addressIsValid}
                                        customerBillingStreetOne={customerBillingStreetOne}
                                        billingAddressIsValid={billingAddressIsValid}
                                        shippingRates={shippingRates}
                                        selectedShippingRateID={selectedShippingRateID}
                                        selectedBillingAddressType={selectedBillingAddressType}
                                        orderCost={displayedOrderCost}
                                        stripePaymentDetailsCompleted={stripePaymentDetailsCompleted}
                                        stripePaymentSucceeded={stripePaymentSucceeded}
                                        hasComplimentaryBookInvite={complimentaryBookInviteQuantity > 0}
                                        snackbarData={snackbarData}
                                        stripeSucceededPaymentIntent={stripeSucceededPaymentIntent}
                                        stripePaymentMethod={stripePaymentMethod}
                                        orderSaleItem={orderSaleItem}
                                        changeCheckoutSessionStatus={changeCheckoutSessionStatus}
                                        preparePromotionsForReview={preparePromotionsForReview}
                                        finishPromotionsForReview={finishPromotionsForReview}
                                        setOrderCost={setOrderCost}
                                        setStripeTaxCalculationId={setStripeTaxCalculationId}
                                        setCustomerStreetOne={setCustomerStreetOne}
                                        setAddressIsValid={setAddressIsValid}
                                        setCustomerBillingStreetOne={setCustomerBillingStreetOne}
                                        setBillingAddressIsValid={setBillingAddressIsValid}
                                        setShippingRates={setShippingRates}
                                        setSelectedShippingRateID={setSelectedShippingRateID}
                                        setSelectedBillingAddressType={setSelectedBillingAddressType}
                                        setStripePaymentDetailsCompleted={setStripePaymentDetailsCompleted}
                                        setCustomerDetailsCompleted={setCustomerDetailsCompleted}
                                        setHadComplimentaryBookInviteForCheckoutFlow={setHadComplimentaryBookInviteForCheckoutFlow}
                                        setSnackbarData={setSnackbarData}
                                        setIsUserProfileDialogExpanded={setIsUserProfileDialogExpanded}
                                        setInputFocused={setInputFocused}
                                        onCursorEnter={onCursorEnter}
                                        onCursorLeave={onCursorLeave}
                                        setCursorIsHidden={setCursorIsHidden}
                                    />
                                </Elements>
                            </>
                        ) : (
                            <PlaceholderCheckpointContainer>
                                <ReactSVG
                                    src={Spinner}
                                    alt="spinner"
                                />
                                <PlaceholderCheckpointText>
                                    {`${checkpointPlaceholderText}${checkpointPlaceholderEllipsis}`}
                                </PlaceholderCheckpointText>
                            </PlaceholderCheckpointContainer>
                        )}
                </CheckpointContainer>
            </ContentContainer>
        </Container>
    );
}

export default CheckoutView;
